// src/pages/patientDashboard/index.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import PublicIcon from "@mui/icons-material/Public";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import MedicationIcon from "@mui/icons-material/Medication";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ScienceIcon from "@mui/icons-material/Science";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import LinkIcon from "@mui/icons-material/Link";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import PrintIcon from "@mui/icons-material/Print";
import PersonIcon from "@mui/icons-material/Person";
import CancelIcon from "@mui/icons-material/Cancel";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import HomeIcon from "@mui/icons-material/Home";
import PaymentIcon from "@mui/icons-material/Payment";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../context/AuthContext";
import { getPatientByUserId, getPatientById, updatePatient, syncPatientTypeForWorkflow } from "../../services/patientService";
import { getAppointmentsByPatientId, createPatientPortalAppointment, getAppointmentsByDoctorAndDate, updateAppointment } from "../../services/appointmentService";
import { getAllDoctors, getDoctorById, getDoctorsByDepartmentIdMultiple, getDoctorByDepartmentId } from "../../services/doctorService";
import { getAllDepartment } from "../../services/departmentService";
import { scheduleRemindersForAppointment } from "../../services/appointmentReminderService";
import { getPathLabReportsForPatient } from "../PathManagePatient/labTestWorkflowUtils";
import { createInvoice, getInvoicesByPatientId } from "../../services/invoiceService";
import { getPrescriptionsByPatientId, getPrescriptionById } from "../../services/prescriptionService";
import { getByPrescriptionId as getComplaintsByPrescriptionId } from "../../services/PrescriptionComplaintService";
import { enrichPrescriptionMedicinesWithNames } from "../../utils/prescriptionMedicineUtils";
import apiClient from "../../services/apiClient";
import { getPatientLabBookings, bookPrescribedLabTests } from "../../services/patientLabBookingService";
import { updatePortalUser } from "../../services/userManagementService";
import { toast } from "react-toastify";
import { confirmAsync } from "../../utils/appNotify";
import * as Yup from "yup";
import { useFormik } from "formik";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PatientPrintPreviewDialog from "./PatientPrintPreviewDialog";
import PatientTimelinePrintMenu from "../../components/PatientTimelinePrintMenu";
import { isPatientProfileIncomplete } from "./PatientAddProfile";
import PatientPortalHeader from "../patientPortal/PatientPortalHeader";
import PatientDoctorSearchPanel from "../patientPortal/PatientDoctorSearchPanel";

import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import {
  LARGE_FORM_MODAL_SX,
} from "../../utils/formModalStyles";
import {
  canPatientCancelAppointment,
  canPatientRescheduleAppointment,
  getDisplayAppointmentStatus,
  processPatientLabReports,
  buildPatientAppointmentUpdatePayload,
  buildInvoicePaymentLookup,
  getAppointmentInvoicePaymentMode,
  buildPatientSelfBookLabTestPayload,
  getShortComplaintsText,
  getPrescriptionDisplayId,
  getPrescriptionDisplayDate,
  getPrescriptionDoctorName,
  getLabTestListStatusLabel,
  mergePatientDashboardLabTests,
  buildPrescriptionOpdPrintDayData,
  normalizePrescriptionTextEntry,
  matchPrescriptionsToAppointment,
  matchLabReportsToAppointment,
  validatePrescriptionForLabBooking,
  isClosedPatientAppointment,
} from "./appointmentPatientUtils";
import {
  autoCancelPastAppointments,
  cancelAppointmentSoft,
  getAppointmentCancelReasonText,
  isAppointmentCancelled,
  isAppointmentCompleted,
  isAppointmentRejected,
} from "../../utils/appointmentCancelUtils";
import {
  fetchOrganizationName,
  fetchOrganizationProfile,
  getOrganizationId,
  getOrganizationName,
} from "../../utils/organizationUtils";
import { rememberInvoiceDepartment } from "../../utils/invoiceDepartmentMemory";
import { printPathologyLabReport, enrichReportsWithDoctorNames, buildPathologyLabReportHtml } from "../path-lab/labReportDisplayUtils";
import {
  buildOpdSummaryDocumentHtml,
  buildPatientDocumentStyles,
  exportPatientSummaryDocumentToPdf,
  getPatientPdfFilename,
  printPatientSummaryDocument,
} from "../../utils/patientSummaryDocumentUtils";
import {
  bindPatientToUser,
  getBoundPatientId,
  patientRecordMatchesUser,
  readPendingAppointment,
  clearPendingAppointment,
  savePendingAppointment,
  getBookingOrganizationId,
  clearBookingOrganizationId,
} from "../../utils/patientPortalSession";

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

const PATIENT_DASHBOARD_REFRESH_MS = 30000;

const PRINT_SEARCH_FIELD_SX = {
  flex: "1 1 180px",
  minWidth: { xs: "100%", sm: 160 },
  "& .MuiOutlinedInput-root": {
    height: 40,
    backgroundColor: "#fff",
    fontSize: "0.875rem",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
  },
};

const wrapOpdPrintDocumentHtml = (bodyHtml) =>
  `<!DOCTYPE html><html><head><meta charset="utf-8" /><style>${buildPatientDocumentStyles()}</style></head><body>${bodyHtml || ""}</body></html>`;

const SPECIALITIES = [
  { label: "Cardiac Care", icon: <FavoriteBorderIcon fontSize="small" color="error" /> },
  { label: "Cancer Care", icon: <LocalHospitalIcon fontSize="small" color="secondary" /> },
  { label: "Neurosciences", icon: <PsychologyIcon fontSize="small" color="primary" /> },
  { label: "Gastro Sciences", icon: <MedicationIcon fontSize="small" color="success" /> },
  { label: "Orthopaedics", icon: <AssignmentOutlinedIcon fontSize="small" color="warning" /> },
  { label: "Renal Care", icon: <ScienceIcon fontSize="small" color="info" /> },
];

const QUICK_ACTIONS = [
  {
    key: "search-doctor",
    title: "Search Doctor",
    description: "Find the right specialist tailored to your need.",
    icon: <SearchIcon />,
    highlighted: false,
    hidden: true, // Hidden from quick actions display but used by other features
  },
  {
    key: "book-appointment",
    title: "Book Appointment",
    description: "Schedule an in-person visit with ease.",
    icon: <CheckCircleIcon />,
    highlighted: true, // Orange button
  },
  // {
  //   key: "second-opinion",
  //   title: "Second Opinion",
  //   description: "Get expert medical advice from top specialists.",
  //   icon: <PersonIcon />,
  //   highlighted: false, // White button
  // },
  // {
  //   key: "health-checkup",
  //   title: "Get Health Checkup",
  //   description: "Comprehensive health screening packages.",
  //   icon: <HealthAndSafetyIcon />,
  //   highlighted: false, // White button
  // },
  {
    key: "virtual-consult",
    title: "Book A Virtual Consultation",
    description: "Consult online from the comfort of your home.",
    icon: <ScienceIcon />, // Using beaker icon like in the image
    highlighted: true, // Orange button
  },
  // {
  //   key: "homecare",
  //   title: "Homecare",
  //   description: "Medical care services at your doorstep.",
  //   icon: <HomeIcon />,
  //   highlighted: false, // White button
  // },
  {
    key: "book-test",
    title: "Book a Test",
    description: "Plan diagnostics and health check-ups quickly.",
    icon: <MedicationIcon />, // Using pill icon like in the image
    highlighted: false, // White button
  },
];


const PATIENT_LANGUAGE_STORAGE_KEY = "hms_patient_language";

const PATIENT_PORTAL_TRANSLATIONS = {
  en: {
    Modules: "Modules",
    Doctors: "Doctors",
    "My Profile": "My Profile",
    "Edit Profile": "Edit Profile",
    About: "About",
    Dashboard: "Dashboard",
    Logout: "Logout",
    "Sign In": "Sign In",
    "Select Language": "Select Language",
    English: "English",
    Hindi: "Hindi",
    Marathi: "Marathi",
    "For Patients": "For Patients",
    "Find a Doctor": "Find a Doctor",
    "Book Appointment": "Book Appointment",
    OPD: "OPD",
    Pathology: "Pathology",
    Specialities: "Specialities",
    Appointments: "Appointments",
    Pharmacy: "Pharmacy",
    Laboratory: "Laboratory",
    Billing: "Billing",
    "Care at HMS": "Care at HMS",
    "Hospital Management System - patient portal, clinical records, pharmacy, laboratory and billing in a single suite.": "Hospital Management System - patient portal, clinical records, pharmacy, laboratory and billing in a single suite.",
    "Book specialists, manage visits and keep prescriptions and lab reports in one patient portal.": "Book specialists, manage visits and keep prescriptions and lab reports in one patient portal.",
    "All Rights Reserved.": "All Rights Reserved.",
    Department: "Department",
    "All departments": "All departments",
    "Available Doctor": "Available Doctor",
    "No doctor available for this department": "No doctor available for this department",
    "No matching doctors": "No matching doctors",
    "Select doctor": "Select doctor",
    "Search Doctor or Department": "Search Doctor or Department",
    Departments: "Departments",
    "No results found": "No results found",
    Book: "Book",
    "Search and select a doctor or speciality first.": "Search and select a doctor or speciality first.",
    "Selected doctor could not be found. Please search again.": "Selected doctor could not be found. Please search again.",
    "Welcome {name}, How can we help you today?": "Welcome {name}, How can we help you today?",
    "Book a Virtual Consultation": "Book a Virtual Consultation",
    "Book A Virtual Consultation": "Book a Virtual Consultation",
    "Upcoming Appointments": "Upcoming Appointments",
    "Previous Consultations": "Previous Consultations",
    "Booked Tests": "Booked Tests",
    "Booked by patient": "Booked by patient",
    "No records yet.": "No records yet.",
    All: "All",
    Schedule: "Schedule",
    Filter: "Filter",
    "Download / Print": "Download / Print",
    "Select document": "Select document",
    "Enter the prescription ID, then choose what to print or download.": "Enter the prescription ID, then choose what to print or download.",
    "Show documents": "Show documents",
    View: "View",
    Edit: "Edit",
    Reschedule: "Reschedule",
    Cancel: "Cancel",
    Virtual: "Virtual",
    Join: "Join",
    Date: "Date",
    Test: "Test",
    Status: "Status",
    Reason: "Reason",
    Completed: "Completed",
    Cancelled: "Cancelled",
    Rejected: "Rejected",
    "No show": "No show",
    "Pending Confirmation": "Pending Confirmation",
    Scheduled: "Scheduled",
    Confirmed: "Confirmed",
    CONFIRMED: "Confirmed",
    "Called by patient": "Called by patient",
    Action: "Action",
    "Prescription ID": "Prescription ID",
    "Enter your prescription ID, select a doctor, then choose a slot.": "Enter your prescription ID, select a doctor, then choose a slot.",
    "The prescribing doctor and department are filled in from the prescription. Select the tests you want to book.": "The prescribing doctor and department are filled in from the prescription. Select the tests you want to book.",
    "Tests prescribed by doctor": "Tests prescribed by doctor",
    "Select prescribed test(s)": "Select prescribed test(s)",
    "Please select at least one prescribed test.": "Please select at least one prescribed test.",
    "Please enter a prescription ID.": "Please enter a prescription ID.",
    "Patient information not found. Please contact support.": "Patient information not found. Please contact support.",
    "This prescription was not found for your patient account.": "This prescription was not found for your patient account.",
    "No lab tests were prescribed on this prescription. Test booking is not allowed.": "No lab tests were prescribed on this prescription. Test booking is not allowed.",
    "A test can be booked only after 1 day has passed from when the doctor prescribed it.": "A test can be booked only after 1 day has passed from when the doctor prescribed it.",
    "You can book this test only after 1 day has passed from when the doctor prescribed it. Booking opens on {date}.": "You can book this test only after 1 day has passed from when the doctor prescribed it. Booking opens on {date}.",
    "Completed tests do not need to be booked again. Only pending tests can be booked.": "Completed tests do not need to be booked again. Only pending tests can be booked.",
    "Completed tests do not need to be booked again. Select the pending tests you want.": "Completed tests do not need to be booked again. Select the pending tests you want.",
    "Completed — booking not required": "Completed — booking not required",
    "Already in progress": "Already in progress",
    "Prescription ID is invalid or could not be verified.": "Prescription ID is invalid or could not be verified.",
    "Dr Name": "Dr Name",
    Complaints: "Complaints",
    "Virtual appointments require online payment only.": "Virtual appointments require online payment only.",
    Search: "Search",
    Logout: "Logout",
  },
  hi: {
    Modules: "मॉड्यूल्स",
    Doctors: "डॉक्टर",
    "My Profile": "मेरी प्रोफाइल",
    "Edit Profile": "प्रोफ़ाइल संपादित करें",
    About: "हमारे बारे में",
    Dashboard: "डैशबोर्ड",
    Logout: "लॉगआउट",
    "Sign In": "साइन इन",
    "Select Language": "भाषा चुनें",
    English: "अंग्रेज़ी",
    Hindi: "हिंदी",
    Marathi: "मराठी",
    "For Patients": "मरीज़ों के लिए",
    "Find a Doctor": "डॉक्टर खोजें",
    "Book Appointment": "अपॉइंटमेंट बुक करें",
    OPD: "OPD",
    Pathology: "पैथोलॉजी",
    Specialities: "विशेषज्ञताएँ",
    Appointments: "अपॉइंटमेंट",
    Pharmacy: "फार्मेसी",
    Laboratory: "प्रयोगशाला",
    Billing: "बिलिंग",
    "Care at HMS": "HMS में स्वास्थ्य सेवा",
    "Hospital Management System - patient portal, clinical records, pharmacy, laboratory and billing in a single suite.": "हॉस्पिटल मैनेजमेंट सिस्टम - पेशेंट पोर्टल, क्लिनिकल रिकॉर्ड, फार्मेसी, प्रयोगशाला और बिलिंग एक ही प्लेटफॉर्म पर।",
    "Book specialists, manage visits and keep prescriptions and lab reports in one patient portal.": "विशेषज्ञ बुक करें, विज़िट प्रबंधित करें और प्रिस्क्रिप्शन व लैब रिपोर्ट एक ही पेशेंट पोर्टल में रखें।",
    "All Rights Reserved.": "सर्वाधिकार सुरक्षित।",
    Department: "विभाग",
    "All departments": "सभी विभाग",
    "Available Doctor": "उपलब्ध डॉक्टर",
    "No doctor available for this department": "इस विभाग के लिए कोई डॉक्टर उपलब्ध नहीं है",
    "No matching doctors": "कोई मिलान वाला डॉक्टर नहीं",
    "Select doctor": "डॉक्टर चुनें",
    "Search Doctor or Department": "डॉक्टर या विभाग खोजें",
    Departments: "विभाग",
    "No results found": "कोई परिणाम नहीं मिला",
    Book: "बुक करें",
    "Search and select a doctor or speciality first.": "पहले डॉक्टर या विशेषज्ञता खोजकर चुनें।",
    "Selected doctor could not be found. Please search again.": "चयनित डॉक्टर नहीं मिला। कृपया फिर से खोजें।",
    "Welcome {name}, How can we help you today?": "स्वागत है {name}, आज हम आपकी कैसे मदद कर सकते हैं?",
    "Book a Virtual Consultation": "वर्चुअल परामर्श बुक करें",
    "Book A Virtual Consultation": "वर्चुअल परामर्श बुक करें",
    "Book a Test": "टेस्ट बुक करें",
    "Upcoming Appointments": "आगामी अपॉइंटमेंट",
    "Previous Consultations": "पिछली परामर्श",
    "Booked Tests": "बुक किए गए टेस्ट",
    "Booked by patient": "मरीज़ द्वारा बुक",
    "No records yet.": "अभी कोई रिकॉर्ड नहीं है।",
    All: "सभी",
    Schedule: "शेड्यूल",
    Filter: "फ़िल्टर",
    "Download / Print": "डाउनलोड / प्रिंट",
    "Select document": "दस्तावेज़ चुनें",
    "Enter the prescription ID, then choose what to print or download.": "प्रिस्क्रिप्शन आईडी दर्ज करें, फिर चुनें कि क्या प्रिंट या डाउनलोड करना है।",
    "Show documents": "दस्तावेज़ दिखाएँ",
    View: "देखें",
    Edit: "संपादित करें",
    Reschedule: "रीशेड्यूल",
    Cancel: "रद्द करें",
    Virtual: "वर्चुअल",
    Join: "जॉइन",
    Date: "तारीख",
    Test: "टेस्ट",
    Status: "स्थिति",
    Reason: "कारण",
    Completed: "पूर्ण",
    Cancelled: "रद्द",
    Rejected: "अस्वीकृत",
    "No show": "उपस्थित नहीं हुए",
    "Pending Confirmation": "पुष्टि लंबित",
    Scheduled: "निर्धारित",
    Confirmed: "पुष्ट",
    CONFIRMED: "पुष्ट",
    "Called by patient": "मरीज़ द्वारा रद्द",
    Action: "कार्रवाई",
    "Prescription ID": "प्रिस्क्रिप्शन आईडी",
    "Enter your prescription ID, select a doctor, then choose a slot.": "अपना प्रिस्क्रिप्शन आईडी दर्ज करें, डॉक्टर चुनें, फिर स्लॉट चुनें।",
    "The prescribing doctor and department are filled in from the prescription. Select the tests you want to book.": "प्रिस्क्रिप्शन से डॉक्टर और विभाग भर जाते हैं। जो टेस्ट बुक करने हैं उन्हें चुनें।",
    "Tests prescribed by doctor": "डॉक्टर द्वारा लिखे गए टेस्ट",
    "Select prescribed test(s)": "लिखे गए टेस्ट चुनें",
    "Please select at least one prescribed test.": "कृपया कम से कम एक लिखा हुआ टेस्ट चुनें।",
    "Please enter a prescription ID.": "कृपया प्रिस्क्रिप्शन आईडी दर्ज करें।",
    "Patient information not found. Please contact support.": "मरीज़ की जानकारी नहीं मिली। कृपया सहायता से संपर्क करें।",
    "This prescription was not found for your patient account.": "यह प्रिस्क्रिप्शन आपके मरीज़ खाते के लिए नहीं मिला।",
    "No lab tests were prescribed on this prescription. Test booking is not allowed.": "इस प्रिस्क्रिप्शन पर कोई लैब टेस्ट नहीं लिखा गया। टेस्ट बुकिंग की अनुमति नहीं है।",
    "A test can be booked only after 1 day has passed from when the doctor prescribed it.": "डॉक्टर द्वारा टेस्ट लिखे जाने के 1 दिन बाद ही टेस्ट बुक किया जा सकता है।",
    "You can book this test only after 1 day has passed from when the doctor prescribed it. Booking opens on {date}.": "डॉक्टर द्वारा टेस्ट लिखे जाने के 1 दिन बाद ही आप यह टेस्ट बुक कर सकते हैं। बुकिंग {date} को खुलेगी।",
    "Completed tests do not need to be booked again. Only pending tests can be booked.": "पूर्ण हो चुके टेस्ट दोबारा बुक करने की जरूरत नहीं है। केवल लंबित टेस्ट बुक किए जा सकते हैं।",
    "Completed tests do not need to be booked again. Select the pending tests you want.": "पूर्ण हो चुके टेस्ट दोबारा बुक करने की जरूरत नहीं है। जो लंबित टेस्ट बुक करने हैं उन्हें चुनें।",
    "Completed — booking not required": "पूर्ण — बुकिंग जरूरी नहीं",
    "Already in progress": "पहले से प्रक्रिया में",
    "Prescription ID is invalid or could not be verified.": "प्रिस्क्रिप्शन आईडी अमान्य है या सत्यापित नहीं हो सकी।",
    "Dr Name": "डॉक्टर का नाम",
    Complaints: "शिकायतें",
    "Virtual appointments require online payment only.": "वर्चुअल अपॉइंटमेंट के लिए केवल ऑनलाइन भुगतान आवश्यक है।",
    Search: "खोजें",
    Logout: "लॉगआउट",
  },
  mr: {
    Modules: "मॉड्यूल्स",
    Doctors: "डॉक्टर",
    "My Profile": "माझी प्रोफाइल",
    "Edit Profile": "प्रोफाइल संपादित करा",
    About: "आमच्याबद्दल",
    Dashboard: "डॅशबोर्ड",
    Logout: "लॉगआउट",
    "Sign In": "साइन इन",
    "Select Language": "भाषा निवडा",
    English: "इंग्रजी",
    Hindi: "हिंदी",
    Marathi: "मराठी",
    "For Patients": "रुग्णांसाठी",
    "Find a Doctor": "डॉक्टर शोधा",
    "Book Appointment": "अपॉइंटमेंट बुक करा",
    OPD: "OPD",
    Pathology: "पॅथॉलॉजी",
    Specialities: "तज्ज्ञता",
    Appointments: "अपॉइंटमेंट",
    Pharmacy: "फार्मसी",
    Laboratory: "लॅबोरेटरी",
    Billing: "बिलिंग",
    "Care at HMS": "HMS मधील आरोग्यसेवा",
    "Hospital Management System - patient portal, clinical records, pharmacy, laboratory and billing in a single suite.": "हॉस्पिटल मॅनेजमेंट सिस्टम - पेशंट पोर्टल, क्लिनिकल नोंदी, फार्मसी, लॅबोरेटरी आणि बिलिंग एका प्लॅटफॉर्मवर.",
    "Book specialists, manage visits and keep prescriptions and lab reports in one patient portal.": "तज्ज्ञ बुक करा, भेटी व्यवस्थापित करा आणि प्रिस्क्रिप्शन व लॅब रिपोर्ट एका पेशंट पोर्टलमध्ये ठेवा.",
    "All Rights Reserved.": "सर्व हक्क राखीव.",
    Department: "विभाग",
    "All departments": "सर्व विभाग",
    "Available Doctor": "उपलब्ध डॉक्टर",
    "No doctor available for this department": "या विभागासाठी डॉक्टर उपलब्ध नाही",
    "No matching doctors": "जुळणारे डॉक्टर नाहीत",
    "Select doctor": "डॉक्टर निवडा",
    "Search Doctor or Department": "डॉक्टर किंवा विभाग शोधा",
    Departments: "विभाग",
    "No results found": "परिणाम सापडला नाही",
    Book: "बुक करा",
    "Search and select a doctor or speciality first.": "प्रथम डॉक्टर किंवा तज्ज्ञता शोधून निवडा.",
    "Selected doctor could not be found. Please search again.": "निवडलेला डॉक्टर सापडला नाही. कृपया पुन्हा शोधा.",
    "Welcome {name}, How can we help you today?": "स्वागत आहे {name}, आज आम्ही तुमची कशी मदत करू शकतो?",
    "Book a Virtual Consultation": "व्हर्च्युअल सल्ला बुक करा",
    "Book A Virtual Consultation": "व्हर्च्युअल सल्ला बुक करा",
    "Book a Test": "टेस्ट बुक करा",
    "Upcoming Appointments": "आगामी अपॉइंटमेंट",
    "Previous Consultations": "मागील सल्लामसलत",
    "Booked Tests": "बुक केलेल्या चाचण्या",
    "Booked by patient": "रुग्णाने बुक केले",
    "No records yet.": "अद्याप कोणतेही रेकॉर्ड नाही.",
    All: "सर्व",
    Schedule: "शेड्यूल",
    Filter: "फिल्टर",
    "Download / Print": "डाउनलोड / प्रिंट",
    "Select document": "दस्तऐवज निवडा",
    "Enter the prescription ID, then choose what to print or download.": "प्रिस्क्रिप्शन आयडी टाका, नंतर काय प्रिंट किंवा डाउनलोड करायचे ते निवडा.",
    "Show documents": "दस्तऐवज दाखवा",
    View: "पहा",
    Edit: "संपादित करा",
    Reschedule: "रीशेड्यूल",
    Cancel: "रद्द करा",
    Virtual: "व्हर्च्युअल",
    Join: "जॉइन",
    Date: "तारीख",
    Test: "चाचणी",
    Status: "स्थिती",
    Reason: "कारण",
    Completed: "पूर्ण",
    Cancelled: "रद्द",
    Rejected: "नाकारले",
    "No show": "उपस्थित राहिले नाही",
    "Pending Confirmation": "पुष्टी प्रलंबित",
    Scheduled: "नियोजित",
    Confirmed: "पुष्ट",
    CONFIRMED: "पुष्ट",
    "Called by patient": "रुग्णाने रद्द केले",
    Action: "कृती",
    "Prescription ID": "प्रिस्क्रिप्शन आयडी",
    "Enter your prescription ID, select a doctor, then choose a slot.": "तुमचा प्रिस्क्रिप्शन आयडी टाका, डॉक्टर निवडा, नंतर स्लॉट निवडा.",
    "The prescribing doctor and department are filled in from the prescription. Select the tests you want to book.": "प्रिस्क्रिप्शनवरून डॉक्टर आणि विभाग भरले जातात. बुक करायच्या चाचण्या निवडा.",
    "Tests prescribed by doctor": "डॉक्टरांनी लिहिलेल्या चाचण्या",
    "Select prescribed test(s)": "लिहिलेल्या चाचण्या निवडा",
    "Please select at least one prescribed test.": "कृपया किमान एक लिहिलेली चाचणी निवडा.",
    "Please enter a prescription ID.": "कृपया प्रिस्क्रिप्शन आयडी टाका.",
    "Patient information not found. Please contact support.": "रुग्णाची माहिती सापडली नाही. कृपया मदतीसाठी संपर्क करा.",
    "This prescription was not found for your patient account.": "हे प्रिस्क्रिप्शन तुमच्या रुग्ण खात्यासाठी सापडले नाही.",
    "No lab tests were prescribed on this prescription. Test booking is not allowed.": "या प्रिस्क्रिप्शनवर कोणतीही लॅब चाचणी लिहिली नाही. चाचणी बुकिंगला परवानगी नाही.",
    "A test can be booked only after 1 day has passed from when the doctor prescribed it.": "डॉक्टरांनी चाचणी लिहिल्यानंतर 1 दिवस पूर्ण झाल्यावरच चाचणी बुक करता येते.",
    "You can book this test only after 1 day has passed from when the doctor prescribed it. Booking opens on {date}.": "डॉक्टरांनी चाचणी लिहिल्यानंतर 1 दिवस पूर्ण झाल्यावरच तुम्ही ही चाचणी बुक करू शकता. बुकिंग {date} रोजी सुरू होईल.",
    "Completed tests do not need to be booked again. Only pending tests can be booked.": "पूर्ण झालेल्या चाचण्या पुन्हा बुक करण्याची गरज नाही. फक्त प्रलंबित चाचण्या बुक करता येतात.",
    "Completed tests do not need to be booked again. Select the pending tests you want.": "पूर्ण झालेल्या चाचण्या पुन्हा बुक करण्याची गरज नाही. बुक करायच्या प्रलंबित चाचण्या निवडा.",
    "Completed — booking not required": "पूर्ण — बुकिंग आवश्यक नाही",
    "Already in progress": "आधीच प्रक्रियेत",
    "Prescription ID is invalid or could not be verified.": "प्रिस्क्रिप्शन आयडी अवैध आहे किंवा पडताळता आला नाही.",
    "Dr Name": "डॉक्टरचे नाव",
    Complaints: "तक्रारी",
    "Virtual appointments require online payment only.": "व्हर्च्युअल अपॉइंटमेंटसाठी फक्त ऑनलाइन पेमेंट आवश्यक आहे.",
    Search: "शोधा",
  },
};

const patientPortalTranslate = (language, key, vars) => {
  let text =
    PATIENT_PORTAL_TRANSLATIONS[language]?.[key] ??
    PATIENT_PORTAL_TRANSLATIONS.en[key] ??
    key;
  if (vars) {
    Object.entries(vars).forEach(([name, value]) => {
      text = String(text).replace(new RegExp(`\\{${name}\\}`, "g"), String(value ?? ""));
    });
  }
  return text;
};

const PatientPortalFooter = ({ language, onFooterLink }) => {
  const navigate = useNavigate();
  const t = (key) => patientPortalTranslate(language, key);
  const goToLandingSection = (section) => {
    const path = String(window.location.pathname || "").replace(/\/$/, "");
    if (path.endsWith("/landing")) {
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    navigate(`/landing#${section}`);
  };
  const handleFooterLink = (item) => {
    if (onFooterLink?.(item)) return;
    goToLandingSection(
      item === "Find a Doctor" || item === "Book Appointment" ? "doctor-section" : "modules-section"
    );
  };

  return (
    <Box id="patient-portal-footer" sx={{ backgroundColor: "#F5F7FA", color: "#2F3A4A", py: 5, borderTop: "1px solid #E5E9EF" }}>
      <Box sx={{ px: { xs: 3, md: 6, lg: 10 } }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "6px",
                  backgroundColor: "#0B78D0",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LocalHospitalIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography
                sx={{ fontSize: 22, fontWeight: 800, color: "#2F3A4A", cursor: "pointer" }}
                onClick={() => navigate("/landing")}
              >
                HMS
              </Typography>
            </Box>
            <Typography sx={{ maxWidth: 460, fontSize: 13, lineHeight: 1.7, color: "#6B7280" }}>
              {t("Hospital Management System - patient portal, clinical records, pharmacy, laboratory and billing in a single suite.")}
            </Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography sx={{ fontWeight: 700, mb: 1.5, color: "#2F3A4A" }}>{t("For Patients")}</Typography>
            {["Find a Doctor", "Book Appointment", "OPD", "Pathology"].map((item) => (
              <Typography
                key={item}
                onClick={() => handleFooterLink(item)}
                sx={{ fontSize: 13, color: "#6B7280", mb: 0.8, cursor: "pointer", "&:hover": { color: "#0B78D0" } }}
              >
                {t(item)}
              </Typography>
            ))}
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography sx={{ fontWeight: 700, mb: 1.5, color: "#2F3A4A" }}>{t("Specialities")}</Typography>
            {["Appointments", "Pharmacy", "Laboratory", "Billing"].map((item) => (
              <Typography
                key={item}
                onClick={() => handleFooterLink(item)}
                sx={{ fontSize: 13, color: "#6B7280", mb: 0.8, cursor: "pointer", "&:hover": { color: "#0B78D0" } }}
              >
                {t(item)}
              </Typography>
            ))}
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography sx={{ fontWeight: 700, mb: 1.5, color: "#2F3A4A" }}>{t("Care at HMS")}</Typography>
            <Typography sx={{ fontSize: 13, lineHeight: 1.7, color: "#6B7280" }}>
              {t("Book specialists, manage visits and keep prescriptions and lab reports in one patient portal.")}
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3, borderColor: "#E5E9EF" }} />
        <Typography sx={{ fontSize: 12, color: "#6B7280", textAlign: "center" }}>
          © {new Date().getFullYear()} HMS - Hospital Management System. {t("All Rights Reserved.")}
        </Typography>
      </Box>
    </Box>
  );
};

const PatientDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const [language, setLanguage] = useState(() => {
    try {
      return localStorage.getItem(PATIENT_LANGUAGE_STORAGE_KEY) || "en";
    } catch {
      return "en";
    }
  });

  const changeLanguage = (nextLanguage) => {
    setLanguage(nextLanguage);
    try {
      localStorage.setItem(PATIENT_LANGUAGE_STORAGE_KEY, nextLanguage);
    } catch {
      // Keep the selected language for the current session if storage is unavailable.
    }
  };

  const t = (key, vars) => patientPortalTranslate(language, key, vars);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patientDetails, setPatientDetails] = useState(null);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);
  const [upcomingPage, setUpcomingPage] = useState(0);
  const [consultationStatusFilter, setConsultationStatusFilter] = useState("completed");
  const [consultationPage, setConsultationPage] = useState(0);
  const [consultationFilterAnchor, setConsultationFilterAnchor] = useState(null);
  const [bookedTestStatusFilter, setBookedTestStatusFilter] = useState("booked");
  const [bookedTestPage, setBookedTestPage] = useState(0);
  const [bookedTestFilterAnchor, setBookedTestFilterAnchor] = useState(null);
  const [printPrescriptionIdInput, setPrintPrescriptionIdInput] = useState("");
  const [printLookupLoading, setPrintLookupLoading] = useState(false);
  const [printLookupPrescription, setPrintLookupPrescription] = useState(null);
  const printLookupIdRef = useRef("");
  const [activeQuickAction, setActiveQuickAction] = useState(null);
  
  // Modal states
  const [doctors, setDoctors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchDoctorQuery, setSearchDoctorQuery] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [appointmentForm, setAppointmentForm] = useState({
    departmentId: "",
    doctorId: "",
    appointmentDate: null,
    appointmentTime: "",
    isVirtual: false,
    paymentMethod: "pay-at-hospital", // "pay-at-hospital" or "pay-online"
  });
  const [filteredDoctorsForAppointment, setFilteredDoctorsForAppointment] = useState([]);
  const [selectedDoctorDetails, setSelectedDoctorDetails] = useState(null);
  const [existingAppointments, setExistingAppointments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedQuickAction, setSelectedQuickAction] = useState("");
  const [bookedTests, setBookedTests] = useState([]); // Store patient's booked tests
  const [patientLabBookings, setPatientLabBookings] = useState([]);
  const [patientPrescriptions, setPatientPrescriptions] = useState([]);
  const [printPreview, setPrintPreview] = useState({
    open: false,
    loading: false,
    title: "",
    documentHtml: "",
    printHandler: null,
  });
  const [orgProfile, setOrgProfile] = useState({ name: "", address: "", phone: "" });
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [openEditProfile, setOpenEditProfile] = useState(false); // Track edit profile modal state

  // Allow the shared patient header on the landing page to open the same
  // existing Edit Profile form without changing the current profile flow.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("editProfile") !== "1") return;

    setOpenEditProfile(true);
    navigate("/patient", { replace: true });
  }, [location.search, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("bookTest") !== "1") return;

    setSelectedQuickAction("book-test");
    setActiveQuickAction(null);
    setRescheduleTarget(null);
    setAppointmentForm({
      departmentId: "",
      doctorId: "",
      appointmentDate: null,
      appointmentTime: "",
      isVirtual: false,
      paymentMethod: "pay-at-hospital",
    });
    navigate("/patient", { replace: true });
  }, [location.search, navigate]);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);

  const activeQuickActionRef = useRef(activeQuickAction);
  const submittingRef = useRef(submitting);
  const openEditProfileRef = useRef(openEditProfile);
  const pendingResumeRef = useRef(false);
  const fetchDashboardDataRef = useRef(null);
  const redirectedHomeRef = useRef(false);

  useEffect(() => {
    activeQuickActionRef.current = activeQuickAction;
  }, [activeQuickAction]);

  useEffect(() => {
    submittingRef.current = submitting;
  }, [submitting]);

  useEffect(() => {
    openEditProfileRef.current = openEditProfile;
  }, [openEditProfile]);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const patientName = useMemo(() => {
    return (
      patientDetails?.name ||
      patientDetails?.firstName ||
      user?.username ||
      "Patient"
    );
  }, [patientDetails, user]);

  const organizationName = useMemo(
    () => orgProfile.name || getOrganizationName(user) || "Hospital",
    [orgProfile.name, user]
  );

  const dashboardLabTests = useMemo(
    () => mergePatientDashboardLabTests(bookedTests, patientLabBookings, patientPrescriptions),
    [bookedTests, patientLabBookings, patientPrescriptions]
  );

  const enrichAndSetBookedTests = async (testsRaw, patientId) => {
    const processed = processPatientLabReports(testsRaw);
    if (!patientId || processed.length === 0) {
      setBookedTests(processed);
      return;
    }

    try {
      const enriched = await enrichReportsWithDoctorNames(
        processed.map((test) => ({
          ...test,
          resultStatus:
            test.resultStatus ||
            (test.status === "COMPLETED" ? "COMPLETE" : test.status),
        })),
        patientId,
        null
      );
      setBookedTests(
        processed.map((test, index) => ({
          ...test,
          doctorName:
            enriched[index]?.doctorName ||
            enriched[index]?.resolvedDoctorName ||
            test.doctorName,
        }))
      );
    } catch {
      setBookedTests(processed);
    }
  };

  const handleViewLabReport = (test) => {
    if (!test) return;
    const reportPayload = {
      testName: test.testName,
      result: test.result,
      reportDate: test.reportDate,
      reportId: test.reportId,
      resultStatus:
        test.resultStatus ||
        (getLabTestListStatusLabel(test) === "Completed" ? "COMPLETE" : test.status || "SCHEDULED"),
      normalRange: test.normalRange,
      patientId: test.patientId || patientDetails?.id,
      doctorName: test.doctorName,
    };
    const printOptions = {
      report: reportPayload,
      patient: patientDetails,
      organizationName,
      hospitalAddress: orgProfile.address,
      hospitalPhone: orgProfile.phone,
    };
    const { title, documentHtml } = buildPathologyLabReportHtml(printOptions);
    setPrintPreview({
      open: true,
      loading: false,
      title: title || "Pathology Report",
      documentHtml,
      printHandler: () => printPathologyLabReport(printOptions),
    });
  };

  const closePrintPreview = () =>
    setPrintPreview({
      open: false,
      loading: false,
      title: "",
      documentHtml: "",
      printHandler: null,
    });

  const findAppointmentForPrescription = (prescription) => {
    const allAppointments = [...upcomingAppointments, ...pastAppointments];
    const appointmentId = prescription?.appointmentId ?? prescription?.appointment_id;
    if (appointmentId != null) {
      const byId = allAppointments.find(
        (item) =>
          Number(item.id) === Number(appointmentId) ||
          Number(item.appointmentData?.id) === Number(appointmentId)
      );
      if (byId) return byId;
    }
    return (
      allAppointments.find(
        (item) => matchPrescriptionsToAppointment([prescription], item).length > 0
      ) || null
    );
  };

  const unwrapPrescriptionRecord = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return value;
    const directId = value.id ?? value.prescriptionId ?? value.prescription_id;
    if (directId != null && directId !== "") return value;
    if (value.data && typeof value.data === "object") return unwrapPrescriptionRecord(value.data);
    if (value.prescription && typeof value.prescription === "object") {
      return unwrapPrescriptionRecord(value.prescription);
    }
    return value;
  };

  const firstFilledList = (source, keys) => {
    if (!source) return [];
    for (const key of keys) {
      const value = source[key];
      if (Array.isArray(value) && value.length) return value;
      if (typeof value === "string" && value.trim()) return [value];
      if (value && typeof value === "object" && !Array.isArray(value)) return [value];
    }
    return [];
  };

  const displayableTextCount = (list) =>
    (Array.isArray(list) ? list : [])
      .map(normalizePrescriptionTextEntry)
      .filter(Boolean).length;

  const displayableMedicineCount = (list) =>
    (Array.isArray(list) ? list : []).filter(
      (item) =>
        item &&
        (item.name ||
          item.medicineName ||
          item.medicine_name ||
          item.medicine ||
          item.drugName ||
          item.medicineId ||
          item.medicine_id)
    ).length;

  const preferRicherList = (primary, fallback, kind = "text") => {
    const score = kind === "medicine" ? displayableMedicineCount : displayableTextCount;
    const primaryScore = score(primary);
    const fallbackScore = score(fallback);
    if (fallbackScore > primaryScore) return fallback;
    if (primaryScore > 0) return primary;
    return fallbackScore > 0 ? fallback : [];
  };

  const fetchPrescriptionRelationList = async (urls) => {
    for (const url of urls) {
      try {
        const response = await apiClient.get(url);
        const data = response?.data;
        if (Array.isArray(data) && data.length) return data;
        if (Array.isArray(data?.data) && data.data.length) return data.data;
        if (Array.isArray(data?.content) && data.content.length) return data.content;
      } catch (error) {
        // Try the next known endpoint. A missing relation must not block printing.
      }
    }
    return [];
  };

  const hydratePrescriptionForPrint = async (prescription) => {
    prescription = unwrapPrescriptionRecord(prescription);
    if (!prescription) return prescription;
    const prescriptionId =
      prescription.id ?? prescription.prescriptionId ?? prescription.prescription_id;
    const listed = patientPrescriptions.find((item) => {
      const listedId = item?.id ?? item?.prescriptionId ?? item?.prescription_id;
      return prescriptionId != null && String(listedId) === String(prescriptionId);
    });

    const complaintKeys = ["complaints", "chiefComplaints", "chiefComplaint", "patientComplaints", "complaint"];
    const diagnosisKeys = ["diagnoses", "diagnosisList", "diagnosis"];
    const medicineKeys = ["medicines", "medicine", "prescriptionMedicines", "prescriptionMedicineList"];
    const labKeys = ["patientLabTests", "labTests", "patient_lab_tests"];
    const vitalKeys = ["vitalSigns", "vitals", "vital_signs"];

    let merged = {
      ...(listed || {}),
      ...prescription,
      complaints: preferRicherList(
        firstFilledList(prescription, complaintKeys),
        firstFilledList(listed, complaintKeys)
      ).map(normalizePrescriptionTextEntry).filter(Boolean),
      diagnoses: preferRicherList(
        firstFilledList(prescription, diagnosisKeys),
        firstFilledList(listed, diagnosisKeys)
      ).map(normalizePrescriptionTextEntry).filter(Boolean),
      medicines: preferRicherList(
        firstFilledList(prescription, medicineKeys),
        firstFilledList(listed, medicineKeys),
        "medicine"
      ),
      patientLabTests: preferRicherList(
        firstFilledList(prescription, labKeys),
        firstFilledList(listed, labKeys),
        "medicine"
      ),
      vitalSigns: preferRicherList(
        firstFilledList(prescription, vitalKeys),
        firstFilledList(listed, vitalKeys),
        "medicine"
      ),
      appointmentId:
        prescription.appointmentId ??
        prescription.appointment_id ??
        listed?.appointmentId ??
        listed?.appointment_id,
      doctorId: prescription.doctorId ?? prescription.doctor_id ?? listed?.doctorId ?? listed?.doctor_id,
      doctorName: prescription.doctorName || listed?.doctorName || listed?.doctor?.name,
      advice: prescription.advice || listed?.advice || "",
      notes: prescription.notes || listed?.notes || "",
      followUpDate: prescription.followUpDate || listed?.followUpDate || listed?.nextVisitDate || "",
    };

    if (prescriptionId != null && prescriptionId !== "") {
      if (!displayableTextCount(merged.complaints)) {
        try {
          const complaints = await getComplaintsByPrescriptionId(prescriptionId);
          merged = {
            ...merged,
            complaints: Array.isArray(complaints) ? complaints : complaints?.data || [],
          };
        } catch (error) {
          console.warn("Could not load complaints for print:", error);
        }
      }
      if (!displayableTextCount(merged.diagnoses)) {
        merged = {
          ...merged,
          diagnoses: await fetchPrescriptionRelationList([
            `/prescription-diagnoses/by-prescription/${prescriptionId}`,
            `/prescription-diagnosis/by-prescription/${prescriptionId}`,
          ]),
        };
      }
      if (!displayableMedicineCount(merged.medicines)) {
        merged = {
          ...merged,
          medicines: await fetchPrescriptionRelationList([
            `/prescription-medicines/by-prescription/${prescriptionId}`,
            `/prescription-medicine/by-prescription/${prescriptionId}`,
          ]),
        };
      }
      if (!merged.vitalSigns.length) {
        merged = {
          ...merged,
          vitalSigns: await fetchPrescriptionRelationList([
            `/prescription-vitalsigns/by-prescription/${prescriptionId}`,
            `/prescription-vitals/by-prescription/${prescriptionId}`,
          ]),
        };
      }
      if (!merged.patientLabTests.length) {
        merged = {
          ...merged,
          patientLabTests: await fetchPrescriptionRelationList([
            `/patient-lab-tests/prescription/${prescriptionId}`,
            `/prescription-lab-tests/by-prescription/${prescriptionId}`,
          ]),
        };
      }
    }

    try {
      const [enriched] = await enrichPrescriptionMedicinesWithNames([merged]);
      return enriched || merged;
    } catch (error) {
      console.warn("Could not resolve medicine names for print:", error);
      return merged;
    }
  };

  const buildVisitSummaryBodyHtml = async ({ prescription = null, appointment = null }) => {
    prescription = await hydratePrescriptionForPrint(prescription);
    let doctorDetails = null;
    const doctorId =
      prescription?.doctorId ??
      prescription?.doctor_id ??
      appointment?.doctorId ??
      appointment?.doctor_id ??
      appointment?.doctor?.id;
    if (doctorId) {
      try {
        doctorDetails = await getDoctorById(doctorId);
      } catch (error) {
        console.warn("Failed to load doctor details for print preview", error);
      }
    }

    const visitDate = appointment?.when?.isValid?.()
      ? appointment.when.format("DD MMM YYYY, hh:mm A")
      : dayjs(prescription?.createdAt || prescription?.prescriptionDate).isValid()
      ? dayjs(prescription.createdAt || prescription.prescriptionDate).format("DD MMM YYYY, hh:mm A")
      : getPrescriptionDisplayDate(prescription) || "—";

    const dayData = prescription
      ? buildPrescriptionOpdPrintDayData(prescription, appointment)
      : {
          prescriptions: [],
          appointments: [
            {
              ...appointment,
              doctorName: appointment?.doctorName || "",
              appointmentDateTime: visitDate,
            },
          ],
          diagnoses: [],
          complaints: [],
          vitalSigns: [],
          LabtestList: matchLabReportsToAppointment(
            dashboardLabTests,
            appointment,
            []
          ).map((test) => ({
            testName: test.testName || test.name || "Lab Test",
          })),
        };

    return buildOpdSummaryDocumentHtml({
      hospitalName: organizationName || "Hospital",
      hospitalAddress: orgProfile.address,
      hospitalPhone: orgProfile.phone,
      patient: {
        id: patientDetails?.id || prescription?.patientId || "N/A",
        name: patientDetails?.name || patientDetails?.fullName || "Patient",
        gender: patientDetails?.gender || "",
        age: patientDetails?.age || "",
        dob: patientDetails?.dob || "",
        mobile: patientDetails?.mobile || patientDetails?.phone || "",
        address: patientDetails?.address || "",
      },
      visitDate,
      dayData,
      doctorDetails,
    });
  };

  const openOpdPrintPreview = async ({
    prescription = null,
    appointment = null,
    title = "Prescription",
  }) => {
    if (!prescription && !appointment) return;

    setPrintPreview({
      open: true,
      loading: true,
      title,
      documentHtml: "",
      printHandler: null,
    });

    try {
      const bodyHtml = await buildVisitSummaryBodyHtml({ prescription, appointment });
      setPrintPreview({
        open: true,
        loading: false,
        title,
        documentHtml: wrapOpdPrintDocumentHtml(bodyHtml),
        printHandler: () =>
          printPatientSummaryDocument({ title: "OPD Summary", bodyHtml }),
      });
    } catch (error) {
      console.error("Failed to open document preview", error);
      toast.error("Unable to open the document.");
      closePrintPreview();
    }
  };

  const printVisitSummaryDocument = async (context) => {
    try {
      const bodyHtml = await buildVisitSummaryBodyHtml(context);
      printPatientSummaryDocument({ title: "OPD Summary", bodyHtml });
    } catch (error) {
      console.error("Failed to print visit summary", error);
      toast.error("Unable to print the document.");
    }
  };

  const exportVisitSummaryPdf = async (context) => {
    try {
      const bodyHtml = await buildVisitSummaryBodyHtml(context);
      await exportPatientSummaryDocumentToPdf({
        bodyHtml,
        filename: getPatientPdfFilename("opd_summary", patientDetails?.id),
      });
    } catch (error) {
      console.error("Failed to export visit summary PDF", error);
      toast.error("Unable to download the document.");
    }
  };

  const hasPrintableDashboardLabResult = (test) => {
    const result = test?.result ?? test?.testResult;
    if (result == null) return false;
    if (typeof result === "object") return Object.keys(result).length > 0;
    const text = String(result).trim().toLowerCase();
    return Boolean(text) && text !== "—" && text !== "-" && text !== "null" && text !== "no results entered";
  };

  const labReportsForVisit = (prescription, appointment) => {
    const matchedPrescriptions = prescription
      ? [prescription]
      : appointment
        ? matchPrescriptionsToAppointment(patientPrescriptions, appointment)
        : [];
    const reports = appointment
      ? matchLabReportsToAppointment(dashboardLabTests, appointment, matchedPrescriptions)
      : (() => {
          const prescriptionRecordId =
            prescription?.id ?? prescription?.prescriptionId ?? prescription?.prescription_id;
          if (prescriptionRecordId == null || prescriptionRecordId === "") return [];
          return dashboardLabTests.filter((test) => {
            const reportPrescriptionId = test?.prescriptionId ?? test?.prescription_id;
            return reportPrescriptionId != null && String(reportPrescriptionId) === String(prescriptionRecordId);
          });
        })();
    return reports.filter(hasPrintableDashboardLabResult);
  };

  const renderVisitPrintMenu = ({
    prescription = null,
    appointment = null,
    iconOnly = false,
    hideTrigger = false,
    externalAnchorEl = null,
    externalOpen = false,
    onMenuClose,
    asField = false,
    fieldLabel = "Select document",
  }) => {
    const resolvedPrescription =
      prescription ||
      (appointment ? matchPrescriptionsToAppointment(patientPrescriptions, appointment)[0] : null) ||
      null;
    const prescriptionRecordId =
      resolvedPrescription?.id ??
      resolvedPrescription?.prescriptionId ??
      resolvedPrescription?.prescription_id ??
      null;
    const appointmentRecord = appointment?.appointmentData || appointment || null;
    const appointmentId =
      appointmentRecord?.id ||
      appointmentRecord?.appointmentId ||
      resolvedPrescription?.appointmentId ||
      resolvedPrescription?.appointment_id ||
      null;
    const when = appointment?.when?.isValid?.()
      ? appointment.when
      : dayjs(
          resolvedPrescription?.createdAt ||
            resolvedPrescription?.prescriptionDate ||
            appointmentRecord?.appointmentDateTime
        );
    const matchedPrescriptions = resolvedPrescription
      ? [resolvedPrescription]
      : [];
    const context = { prescription: resolvedPrescription, appointment };

    return (
      <PatientTimelinePrintMenu
        iconOnly={iconOnly}
        hideTrigger={hideTrigger}
        externalAnchorEl={externalAnchorEl}
        externalOpen={externalOpen}
        onMenuClose={onMenuClose}
        asField={asField}
        fieldLabel={fieldLabel}
        patientId={patientDetails?.id}
        patient={patientDetails}
        dateKey={when?.isValid?.() ? when.format("YYYY-MM-DD") : null}
        appointmentId={appointmentId}
        prescriptionId={prescriptionRecordId}
        prescriptionIds={matchedPrescriptions
          .map((item) => item?.id ?? item?.prescriptionId ?? item?.prescription_id)
          .filter((id) => id != null && id !== "")}
        appointmentDateTime={
          appointmentRecord?.appointmentDateTime ||
          (when?.isValid?.() ? when.format("YYYY-MM-DDTHH:mm:ss") : null)
        }
        labReports={labReportsForVisit(resolvedPrescription, appointment)}
        doctorName={
          resolvedPrescription?.doctorName ||
          appointment?.doctorName ||
          ""
        }
        onPrintSummary={() => printVisitSummaryDocument(context)}
        onExportPdf={() => exportVisitSummaryPdf(context)}
        size="small"
        sx={
          iconOnly
            ? { p: 0, width: 18, height: 18, color: theme.palette.primary.main }
            : undefined
        }
      />
    );
  };

  const lookupPrescriptionForPrint = async (rawId, { notify = false } = {}) => {
    const id = String(rawId || "").replace(/\D/g, "");
    if (!id) {
      setPrintLookupPrescription(null);
      if (notify) toast.error(t("Please enter a prescription ID."));
      return;
    }
    if (!patientDetails?.id) {
      if (notify) toast.error(t("Patient information not found. Please contact support."));
      return;
    }

    printLookupIdRef.current = id;
    setPrintLookupLoading(true);
    try {
      const prescription = unwrapPrescriptionRecord(await getPrescriptionById(id));
      if (printLookupIdRef.current !== id) return;
      const prescriptionPatientId = prescription?.patientId ?? prescription?.patient_id;
      if (!prescription || Number(prescriptionPatientId) !== Number(patientDetails.id)) {
        setPrintLookupPrescription(null);
        if (notify) toast.error(t("This prescription was not found for your patient account."));
        return;
      }
      const fullPrescription = await hydratePrescriptionForPrint(prescription);
      if (printLookupIdRef.current !== id) return;
      setPrintLookupPrescription(fullPrescription);
    } catch (error) {
      console.error("Error loading prescription for print:", error);
      setPrintLookupPrescription(null);
      if (notify) {
        toast.error(
          error?.response?.data?.message ||
            t("This prescription was not found for your patient account.")
        );
      }
    } finally {
      setPrintLookupLoading(false);
    }
  };

  useEffect(() => {
    if (selectedQuickAction !== "download-print") {
      printLookupIdRef.current = "";
      setPrintPrescriptionIdInput("");
      setPrintLookupPrescription(null);
      setPrintLookupLoading(false);
      return undefined;
    }
    const id = String(printPrescriptionIdInput || "").replace(/\D/g, "");
    printLookupIdRef.current = id;
    if (!id) {
      setPrintLookupPrescription(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      lookupPrescriptionForPrint(id);
    }, 450);
    return () => clearTimeout(timer);
  }, [printPrescriptionIdInput, selectedQuickAction, patientDetails?.id]);

  const handleViewPrescription = async (prescription) => {
    if (!prescription) return;
    await openOpdPrintPreview({
      prescription,
      appointment: findAppointmentForPrescription(prescription),
      title: "Prescription",
    });
  };

  // Helper function to parse sub-category results
  const parseSubCategoryResults = (result) => {
    if (!result) return null;
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      
      // Handle full format with subCategoryResults
      if (parsed.subCategoryResults) {
        return parsed;
      }
      
      // Handle compressed format with 's' for subResults and 'r' for remarks
      if (parsed.s || parsed.subResults) {
        const subResultsStr = parsed.s || parsed.subResults || '';
        const subCategoryResults = {};
        if (subResultsStr) {
          subResultsStr.split('|').forEach(item => {
            const [key, value] = item.split(':');
            if (key && value !== undefined) {
              subCategoryResults[key.trim()] = value.trim();
            }
          });
        }
        return {
          subCategoryResults,
          remarks: parsed.r || parsed.remarks || '',
          testName: parsed.testName,
          timestamp: parsed.timestamp
        };
      }
    } catch (e) {
      // If not JSON, return null
    }
    return null;
  };

  // Helper function to format sub-category results for display
  const formatSubCategoryResults = (result) => {
    if (!result) return 'No results entered';
    
    const parsed = parseSubCategoryResults(result);
    if (!parsed || !parsed.subCategoryResults) {
      // If it's a plain string, return it as is
      if (typeof result === 'string' && !result.startsWith('{')) {
        return result;
      }
      return 'No results entered';
    }
    
    const subResults = parsed.subCategoryResults;
    const results = Object.entries(subResults)
      .filter(([key, value]) => value && value.toString().trim() !== '')
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
    
    return results || 'No results entered';
  };

  // Helper function to extract remarks from sub-category results
  const extractRemarks = (result) => {
    if (!result) return '';
    const parsed = parseSubCategoryResults(result);
    return parsed?.remarks || parsed?.r || '';
  };

  const isStrongPassword = (value) =>
    !!value &&
    value.length >= 8 &&
    /[A-Z]/.test(value) &&
    /[a-z]/.test(value) &&
    /\d/.test(value) &&
    /[^A-Za-z0-9]/.test(value);

  const strongPasswordMessage =
    "Use at least 8 characters with an uppercase letter, a lowercase letter, a number, and a special character";

  // Validation schema for profile edit form
  const profileValidationSchema = Yup.object({
    name: Yup.string()
      .required("Name is required")
      .min(2, "Name should be of minimum 2 characters length"),
    gender: Yup.string().required("Gender is required"),
    dob: Yup.date()
      .required("DOB is required")
      .max(dayjs(), "DOB cannot be in the future"),
    address: Yup.string().required("Address is required"),
    aadhar: Yup.string()
      .notRequired()
      .test('aadhar-format', 'Aadhar should be a 12-digit number', function(value) {
        if (!value || value.length === 0) return true;
        return /^\d{12}$/.test(value);
      }),
    email: Yup.string()
      .email("Enter a valid email")
      .notRequired(),
    mobile: Yup.string()
      .notRequired()
      .test('mobile-format', 'Contact Number should be a 10-digit number', function(value) {
        if (!value || value.length === 0) return true;
        return /^\d{10}$/.test(value);
      }),
    currentPassword: Yup.string()
      .notRequired()
      .test('password-change', 'Required when changing password', function(value) {
        const { newPassword, confirmPassword } = this.parent;
        if (newPassword || confirmPassword) {
          return !!value;
        }
        return true;
      }),
    newPassword: Yup.string()
      .notRequired()
      .test('strong-password', strongPasswordMessage, function(value) {
        if (!value) return true;
        return isStrongPassword(value);
      }),
    confirmPassword: Yup.string()
      .notRequired()
      .test('password-match', 'Passwords must match', function(value) {
        const { newPassword } = this.parent;
        if (!value && !newPassword) return true;
        return value === newPassword;
      }),
  });

  // Formik form for profile editing
  const profileFormik = useFormik({
    initialValues: {
      name: patientDetails?.name || "",
      address: patientDetails?.address || "",
      aadhar: patientDetails?.aadhar || "",
      email: patientDetails?.email || "",
      dob: patientDetails?.dob ? dayjs(patientDetails.dob) : null,
      mobile: patientDetails?.mobile || "",
      gender: patientDetails?.gender || "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    enableReinitialize: true,
    validationSchema: profileValidationSchema,
    onSubmit: async (values) => {
      if (!patientDetails?.id) {
        toast.error("Patient information not found. Please contact support.");
        return;
      }

      const patient_req_body = {
        name: values.name,
        mobile: values.mobile || null,
        email: values.email || null,
        aadhar: values.aadhar || null,
        address: values.address,
        dob: dayjs(values.dob).format("YYYY-MM-DD"),
        gender: values.gender,
      };

      try {
        const hasProfileChanged =
          patientDetails.name !== patient_req_body.name ||
          patientDetails.mobile !== patient_req_body.mobile ||
          patientDetails.email !== patient_req_body.email ||
          patientDetails.aadhar !== patient_req_body.aadhar ||
          patientDetails.address !== patient_req_body.address ||
          patientDetails.gender !== patient_req_body.gender ||
          dayjs(patientDetails.dob).format("YYYY-MM-DD") !== patient_req_body.dob;

        // Check if common fields (name, mobile, email) have changed
        const hasCommonFieldsChanged =
          patientDetails.name !== patient_req_body.name ||
          patientDetails.mobile !== patient_req_body.mobile ||
          patientDetails.email !== patient_req_body.email;

        const hasPasswordChanged = values.newPassword && values.newPassword.trim() !== "";

        // Validate password change if provided
        if (hasPasswordChanged) {
          if (!values.currentPassword || values.currentPassword.trim() === "") {
            toast.error("Current password is required to change password.");
            return;
          }

          if (!isStrongPassword(values.newPassword)) {
            toast.error(strongPasswordMessage);
            return;
          }

          if (values.newPassword !== values.confirmPassword) {
            toast.error("New password and confirm password do not match.");
            return;
          }
        }

        // Update patient profile if changed
        if (hasProfileChanged) {
          await updatePatient(patientDetails.id, patient_req_body);
        }

        // Update user table if common fields changed or password changed
        if (hasCommonFieldsChanged || hasPasswordChanged) {
          try {
            const userUpdatePayload = {
              id: user.id,
              full_name: values.name,
              user_role: user.roleName || "patient",
              email: values.email || "",
              department: "",
              mobile: values.mobile || "",
            };

            // Only include password if it's being changed
            if (hasPasswordChanged) {
              userUpdatePayload.user_pwd = values.newPassword;
            }

            await updatePortalUser(userUpdatePayload);
          } catch (userUpdateError) {
            console.error("Error updating user table:", userUpdateError);
            if (hasProfileChanged) {
              toast.warning("Patient profile updated but user table update failed. Please try again.");
            } else if (hasPasswordChanged) {
              toast.error(userUpdateError?.response?.data?.message || "Failed to update password. Please try again.");
              return;
            } else {
              toast.error(userUpdateError?.response?.data?.message || "Failed to update user information. Please try again.");
              return;
            }
          }
        }

        // Show appropriate success message
        if (hasProfileChanged && hasPasswordChanged) {
          toast.success("Profile and password updated successfully!");
        } else if (hasProfileChanged) {
          toast.success("Profile updated successfully!");
        } else if (hasPasswordChanged) {
          toast.success("Password updated successfully!");
        } else {
          toast.info("No changes detected.");
          return;
        }
        
        // Refresh patient details
        try {
          const updatedPatient = await getPatientByUserId(user.id, {
            mobile: user.mobileNumber || user.username,
            organizationId: user.organizationId,
          });
          setPatientDetails(updatedPatient);
        } catch (refreshError) {
          console.error("Error refreshing patient details:", refreshError);
        }
        
        // Reset password fields
        profileFormik.setFieldValue("currentPassword", "");
        profileFormik.setFieldValue("newPassword", "");
        profileFormik.setFieldValue("confirmPassword", "");
        
        setOpenEditProfile(false);
      } catch (error) {
        console.error("Error updating profile:", error);
        toast.error(error?.response?.data?.message || "Failed to update profile. Please try again.");
      }
    },
  });

  const handleCloseEditProfile = () => {
    setOpenEditProfile(false);
    profileFormik.resetForm();
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  useEffect(() => {
    const resolveOrganizationId = () => {
      const fromUser = getOrganizationId(user);
      if (fromUser) return fromUser;

      const fromPatient =
        patientDetails?.organizationId ?? patientDetails?.organization_id;
      if (fromPatient != null && fromPatient !== "" && !Number.isNaN(Number(fromPatient))) {
        const num = Number(fromPatient);
        if (num > 0) return num;
      }

      const fromStorage = localStorage.getItem("organizationId");
      if (fromStorage && !Number.isNaN(Number(fromStorage))) {
        const num = Number(fromStorage);
        if (num > 0) return num;
      }

      return null;
    };

    const loadOrgProfile = async () => {
      const organizationId = resolveOrganizationId();
      if (!organizationId) return;

      try {
        const [profile, orgName] = await Promise.all([
          fetchOrganizationProfile(organizationId),
          fetchOrganizationName(organizationId),
        ]);
        setOrgProfile({
          name: orgName || getOrganizationName(user) || "",
          address: profile?.address || "",
          phone: profile?.phone || "",
        });
      } catch {
        setOrgProfile({
          name: getOrganizationName(user) || "",
          address: "",
          phone: "",
        });
      }
    };

    loadOrgProfile();
  }, [user, patientDetails]);

  useEffect(() => {
    const shouldSkipBackgroundRefresh = () =>
      Boolean(
        activeQuickActionRef.current ||
          submittingRef.current ||
          openEditProfileRef.current
      );

    const fetchData = async (silent = false) => {
      if (!user?.id) {
        if (!silent) {
          setLoading(false);
        }
        return;
      }

      if (!silent) {
        setLoading(true);
        setError(null);
      }

      try {
        let patient = null;
        try {
          patient = await getPatientByUserId(user.id, {
            mobile: user.mobileNumber || user.username,
            organizationId: user.organizationId,
          });
        } catch (patientLookupError) {
          const status = patientLookupError?.response?.status;
          const message = String(
            patientLookupError?.response?.data?.message ||
              patientLookupError?.message ||
              ""
          );
          const isMissingPatient =
            !status ||
            status === 404 ||
            (status === 500 &&
              (message.includes("Failed to retrieve") ||
                message.includes("not found") ||
                message.includes("from table")));
          if (!isMissingPatient) {
            throw patientLookupError;
          }
        }

        if (!patientRecordMatchesUser(patient, user.id, user.mobileNumber || user.username)) {
          const boundId = getBoundPatientId(user.id);
          if (boundId) {
            try {
              patient = await getPatientById(boundId, {
                organizationId: user.organizationId,
              });
            } catch (boundLookupError) {
              patient = null;
            }
          } else {
            patient = null;
          }
        }

        if (patient?.id) {
          bindPatientToUser(user.id, patient.id);
          setPatientDetails(patient);
        } else if (!silent) {
          setPatientDetails(null);
          setUpcomingAppointments([]);
          setPastAppointments([]);
          setLoading(false);
          return;
        } else {
          return;
        }

        const [appointmentsRaw, doctorsRaw, testsRaw, prescriptionsRaw, invoicesRaw, labBookingsRaw] =
          await Promise.all([
          getAppointmentsByPatientId(patient.id).catch(() => []),
          getAllDoctors({ forceRefresh: false }).catch(() => []),
          getPathLabReportsForPatient(patient.id).catch(() => []),
          getPrescriptionsByPatientId(patient.id).catch(() => []),
          getInvoicesByPatientId(patient.id).catch(() => []),
          getPatientLabBookings(patient.id).catch(() => []),
        ]);

        const doctorLookup = {};
        (Array.isArray(doctorsRaw) ? doctorsRaw : []).forEach((doctor) => {
          if (doctor?.id) {
            doctorLookup[doctor.id] = doctor;
          }
        });

        const invoicePaymentLookup = buildInvoicePaymentLookup(invoicesRaw);

        const appointments = Array.isArray(appointmentsRaw)
          ? appointmentsRaw
          : appointmentsRaw
          ? [appointmentsRaw]
          : [];

        // Auto-cancel incomplete appointments only after the appointment day has passed
        try {
          await autoCancelPastAppointments(appointments);
        } catch (e) {
          console.error("Failed to auto-cancel some no-show appointments", e);
        }

        const upcoming = [];
        const history = [];

        appointments.forEach((appointment) => {
          const timestamp =
            appointment?.appointmentDateTime ||
            appointment?.appointmentDate ||
            appointment?.scheduledAt ||
            appointment?.date;

          const schedule = dayjs(timestamp);
          const doctorId = appointment?.doctorId || appointment?.doctor?.id;
          const doctorInfo = doctorLookup[doctorId] || appointment?.doctor || {};

          // Check for virtual appointment flag in multiple possible field names
          // Debug: Log appointment to see structure
          if (appointment?.isVirtual || appointment?.is_virtual || appointment?.virtual) {
            console.log("Virtual appointment found:", appointment);
          }
          
          const isVirtual = appointment?.isVirtual === true || 
                           appointment?.isVirtual === 1 ||
                           appointment?.is_virtual === true ||
                           appointment?.is_virtual === 1 ||
                           appointment?.virtual === true ||
                           appointment?.virtual === 1 ||
                           appointment?.appointmentType === 'virtual' ||
                           appointment?.type === 'virtual' ||
                           String(appointment?.appointmentType || '').toLowerCase() === 'virtual' ||
                           String(appointment?.type || '').toLowerCase() === 'virtual' ||
                           false;
          
          // Check for video link in multiple possible field names
          const videoLink = appointment?.virtualMeeting?.url ||
                           appointment?.virtualMeeting?.link ||
                           appointment?.videoLink || 
                           appointment?.video_link || 
                           appointment?.meetingLink || 
                           appointment?.meeting_link ||
                           appointment?.virtualLink || 
                           appointment?.virtual_link ||
                           appointment?.videoUrl ||
                           appointment?.video_url ||
                           null;

          const enriched = {
            id: appointment?.id || `${timestamp}-${doctorId}`,
            when: schedule,
            rawDate: timestamp,
            doctorName:
              appointment?.doctorName ||
              doctorInfo?.name ||
              "Assigned Doctor",
            departmentName:
              doctorInfo?.departmentName ||
              appointment?.departmentName ||
              "Multi Speciality",
            status: isAppointmentCancelled(appointment) ? "Cancelled" : (appointment?.status || "Scheduled"),
            location: appointment?.location || doctorInfo?.department || "",
            isVirtual: isVirtual,
            videoLink: videoLink,
            // Store full appointment data for cancellation
            appointmentData: appointment,
            patientId: appointment?.patientId,
            doctorId: appointment?.doctorId || doctorId,
            paymentMethod:
              appointment?.paymentMethod || appointment?.payment_method,
            paymentMode:
              getAppointmentInvoicePaymentMode(appointment?.id, invoicePaymentLookup) ||
              appointment?.paymentMode ||
              appointment?.payment_mode,
          };

          if (isClosedPatientAppointment(appointment)) {
            history.push(enriched);
          } else {
            upcoming.push(enriched);
          }
        });

        upcoming.sort((a, b) => a.when.valueOf() - b.when.valueOf());
        history.sort((a, b) => b.when.valueOf() - a.when.valueOf());

        setUpcomingAppointments(upcoming);
        setPastAppointments(history);

        // Process and set booked tests
        await enrichAndSetBookedTests(testsRaw, patient.id);
        setPatientPrescriptions(
          (Array.isArray(prescriptionsRaw) ? prescriptionsRaw : [])
            .map((rx) => ({
              ...rx,
              doctorName: getPrescriptionDoctorName(rx, doctorLookup),
            }))
            .sort((a, b) => {
              const aDate = dayjs(a.createdAt || a.prescriptionDate || 0).valueOf();
              const bDate = dayjs(b.createdAt || b.prescriptionDate || 0).valueOf();
              return bDate - aDate;
            })
        );
        setPatientLabBookings(Array.isArray(labBookingsRaw) ? labBookingsRaw : []);
      } catch (err) {
        console.error("Failed to load patient dashboard data", err);
        if (!silent) {
          setError(
            "We're having trouble loading your dashboard right now. Please try again shortly."
          );
          setUpcomingAppointments([]);
          setPastAppointments([]);
          setBookedTests([]);
          setPatientPrescriptions([]);
          setPatientLabBookings([]);
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    };

    fetchDashboardDataRef.current = fetchData;
    fetchData(false);

    const interval = setInterval(() => {
      if (shouldSkipBackgroundRefresh()) {
        return;
      }
      fetchData(true);
    }, PATIENT_DASHBOARD_REFRESH_MS);

    return () => clearInterval(interval);
  }, [user?.id]);

  useEffect(() => {
    if (loading || redirectedHomeRef.current) return;
    if (!isPatientProfileIncomplete(patientDetails)) return;
    redirectedHomeRef.current = true;
    toast.error("Unable to open the patient dashboard. Please continue from the home page.");
    navigate("/landing", { replace: true });
  }, [loading, patientDetails, navigate]);

  useEffect(() => {
    const resumePendingBooking = async () => {
      if (pendingResumeRef.current) return;

      const pending = readPendingAppointment();
      // Opening a doctor profile only stores a draft. Create the visit after the
      // patient confirms booking, not when they return to the dashboard.
      if (!pending?.confirmOnReturn) {
        if (pending) {
          clearPendingAppointment();
          clearBookingOrganizationId();
        }
        return;
      }

      if (!patientDetails?.id || isPatientProfileIncomplete(patientDetails)) return;

      if (!pending?.doctorId || !pending.appointmentDate || !pending.appointmentTime) return;

      // Only replay a recent booking hand-off. This prevents stale sessionStorage
      // from creating an appointment when the patient simply opens the dashboard.
      const pendingCreatedAt = Number(pending.createdAt || 0);
      const PENDING_BOOKING_MAX_AGE_MS = 2 * 60 * 60 * 1000;
      if (
        !pendingCreatedAt ||
        !Number.isFinite(pendingCreatedAt) ||
        Date.now() - pendingCreatedAt > PENDING_BOOKING_MAX_AGE_MS
      ) {
        clearPendingAppointment();
        clearBookingOrganizationId();
        return;
      }

      pendingResumeRef.current = true;
      clearPendingAppointment();

      try {
        const slotTime = dayjs(
          `${pending.appointmentDate} ${pending.appointmentTime}`,
          "YYYY-MM-DD hh:mm A"
        );
        if (!slotTime.isValid()) {
          clearBookingOrganizationId();
          return;
        }

        const appointmentDateTime = `${pending.appointmentDate}T${slotTime.format("HH:mm")}`;
        const bookingOrganizationId =
          pending.organizationId || getBookingOrganizationId();

        // Make the resume operation idempotent. If the appointment was already
        // created, do not POST /appointments/create again.
        const existingAppointments = await getAppointmentsByPatientId(
          patientDetails.id,
          bookingOrganizationId
        );
        const requestedDateTime = dayjs(appointmentDateTime);
        const alreadyBooked = (Array.isArray(existingAppointments)
          ? existingAppointments
          : []
        ).some((appointment) => {
          const status = String(
            appointment?.status || appointment?.appointmentStatus || ""
          ).toUpperCase();
          const cancelled =
            appointment?.isCancel === true ||
            appointment?.is_cancel === true ||
            ["CANCELLED", "CANCELED", "REJECTED"].includes(status);

          if (cancelled) return false;

          const appointmentPatientId =
            appointment?.patientId ?? appointment?.patient_id;
          if (
            appointmentPatientId != null &&
            String(appointmentPatientId) !== String(patientDetails.id)
          ) {
            return false;
          }

          const existingDateTime =
            appointment?.appointmentDateTime ||
            appointment?.appointment_date_time ||
            appointment?.appointmentDate;

          return (
            existingDateTime &&
            dayjs(existingDateTime).isValid() &&
            dayjs(existingDateTime).isSame(requestedDateTime, "minute")
          );
        });

        if (pending.prescriptionId) {
          const prescription = await getPrescriptionById(pending.prescriptionId);
          const check = validatePrescriptionForLabBooking(prescription, patientDetails.id);
          if (!check.ok) {
            clearPendingAppointment();
            clearBookingOrganizationId();
            toast.error(t(check.message, check.messageVars));
            return;
          }
          const allowedLabTestIds = new Set(
            check.tests.filter((test) => test.bookable !== false).map((test) => Number(test.labTestId))
          );
          const requestedRaw = Array.isArray(pending.labTestIds) ? pending.labTestIds : [];
          const requestedLabTestIds = requestedRaw
            .map((id) => Number(id))
            .filter((id) => allowedLabTestIds.has(id));
          const labTestIds = requestedRaw.length
            ? requestedLabTestIds
            : check.tests.map((test) => test.labTestId);
          if (!labTestIds.length) {
            clearPendingAppointment();
            clearBookingOrganizationId();
            toast.error(
              t("Completed tests do not need to be booked again. Only pending tests can be booked.")
            );
            return;
          }
          await bookPrescribedLabTests({
            patientId: patientDetails.id,
            prescriptionId: pending.prescriptionId,
            labTestIds,
            preferredDate: pending.appointmentDate,
            preferredTime: pending.appointmentTime,
            doctorId: pending.doctorId,
            notes: `Patient portal test booking on ${pending.appointmentDate} at ${pending.appointmentTime}`,
          });
          clearBookingOrganizationId();
          toast.success("Prescribed tests booked successfully.");
          fetchDashboardDataRef.current?.(true);
          return;
        }

        if (alreadyBooked) {
          clearBookingOrganizationId();
          return;
        }

        await createPatientPortalAppointment({
          patientId: patientDetails.id,
          doctorId: pending.doctorId,
          appointmentDateTime,
          isVirtual: Boolean(pending.isVirtual),
          paymentMethod:
            pending.paymentMethod === "pay-online" ? "PAID_ONLINE" : "PAY_AT_HOSPITAL",
          status:
            pending.paymentMethod === "pay-online" ? "CONFIRMED" : "PENDING_CONFIRMATION",
          organizationId: bookingOrganizationId || undefined,
        });
        await syncPatientTypeForWorkflow(patientDetails.id, "OUT", { force: true });
        clearBookingOrganizationId();
        toast.success("Appointment request submitted.");
        fetchDashboardDataRef.current?.(true);
      } catch (error) {
        pendingResumeRef.current = false;
        const backendMessage = String(
          error?.response?.data?.message || error?.message || ""
        ).toLowerCase();
        const isAlreadyBooked =
          backendMessage.includes("already have an appointment") ||
          backendMessage.includes("already have an appointment at the selected time");

        // Do not keep a conflict pending: otherwise every dashboard mount
        // retries the same create request and shows the same popup.
        if (isAlreadyBooked) {
          clearPendingAppointment();
          clearBookingOrganizationId();
          console.warn(
            "Pending appointment was already booked; skipping duplicate creation."
          );
          return;
        }

        // Preserve the existing retry behavior for genuine/transient failures.
        savePendingAppointment(pending);
        console.error("Failed to complete pending appointment:", error);
        toast.error("Could not complete the selected appointment. Please book again.");
      }
    };

    resumePendingBooking();
  }, [patientDetails]);

  // Load initial data for search dropdowns
  useEffect(() => {
    const loadSearchData = async () => {
      try {
        const [doctorsData, departmentsData] = await Promise.all([
          getAllDoctors({ forceRefresh: false }).catch(() => []),
          getAllDepartment().catch(() => []),
        ]);
        setDoctors(Array.isArray(doctorsData) ? doctorsData.filter(d => d.is_active !== 0) : []);
        setDepartments(Array.isArray(departmentsData) ? departmentsData : []);
      } catch (err) {
        console.error("Error loading search data:", err);
      }
    };
    
    loadSearchData();
  }, []);

  // Load data when modals open
  useEffect(() => {
    const loadModalData = async () => {
      if (!activeQuickAction) return;
      
      try {
        if (activeQuickAction.key === "search-doctor" ||
            activeQuickAction.key === "second-opinion" ||
            activeQuickAction.key === "health-checkup") {
          if (doctors.length === 0 || departments.length === 0) {
            const [doctorsData, departmentsData] = await Promise.all([
              getAllDoctors({ forceRefresh: false }).catch(() => []),
              getAllDepartment().catch(() => []),
            ]);
            setDoctors(Array.isArray(doctorsData) ? doctorsData.filter(d => d.is_active !== 0) : []);
            setDepartments(Array.isArray(departmentsData) ? departmentsData : []);
          }
        }
      } catch (err) {
        console.error("Error loading modal data:", err);
      }
    };
    
    loadModalData();
  }, [activeQuickAction, doctors.length, departments.length, patientDetails?.id]);

  const handleQuickAction = (action) => {
    setSelectedQuickAction(action.key); // Track selected button
    if (
      action.key === "book-appointment" ||
      action.key === "virtual-consult" ||
      action.key === "book-test"
    ) {
      setActiveQuickAction(null);
      setRescheduleTarget(null);
      setAppointmentForm({
        departmentId: "",
        doctorId: "",
        appointmentDate: null,
        appointmentTime: "",
        isVirtual: action.key === "virtual-consult",
        paymentMethod: action.key === "virtual-consult" ? "pay-online" : "pay-at-hospital",
      });
      return;
    }
    setActiveQuickAction(action);
    // Reset forms when opening
    const isVirtual = action.key === "virtual-consult";
    setAppointmentForm({
      departmentId: "",
      doctorId: "",
      appointmentDate: null,
      appointmentTime: "",
      isVirtual: isVirtual,
      paymentMethod: isVirtual ? "pay-online" : "pay-at-hospital", // Virtual appointments require online payment
    });
    setSearchDoctorQuery("");
    setSelectedDepartment("");
    setFilteredDoctorsForAppointment([]);
    setSelectedDoctorDetails(null);
    setExistingAppointments([]);
  };

  const scrollToId = (id) => {
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const openBookingSearch = (actionKey = "book-appointment") => {
    const action = QUICK_ACTIONS.find((item) => item.key === actionKey);
    if (action) {
      handleQuickAction(action);
    }
    scrollToId("patient-booking-search");
  };

  const handleCloseModal = () => {
    setActiveQuickAction(null);
    setSearchDoctorQuery("");
    setSelectedDepartment("");
    setAppointmentForm({
      departmentId: "",
      doctorId: "",
      appointmentDate: null,
      appointmentTime: "",
      isVirtual: false,
      paymentMethod: "pay-at-hospital",
    });
    setFilteredDoctorsForAppointment([]);
    setSelectedDoctorDetails(null);
    setExistingAppointments([]);
    setRescheduleTarget(null);
  };

  const fetchDoctorsByDepartment = async (departmentId) => {
    setFilteredDoctorsForAppointment([]);
    setAppointmentForm(prev => ({ ...prev, doctorId: "", appointmentDate: null, appointmentTime: "" }));
    setSelectedDoctorDetails(null);
    setExistingAppointments([]);
    
    try {
      const doctors = await getDoctorsByDepartmentIdMultiple(departmentId);
      setFilteredDoctorsForAppointment(Array.isArray(doctors) ? doctors.filter(d => d.is_active !== 0) : []);
    } catch (error) {
      console.error("Error fetching doctors:", error);
      try {
        const fallbackDoctors = await getDoctorByDepartmentId(departmentId);
        setFilteredDoctorsForAppointment(Array.isArray(fallbackDoctors) ? fallbackDoctors.filter(d => d.is_active !== 0) : []);
      } catch (fallbackError) {
        console.error("Fallback also failed:", fallbackError);
        setFilteredDoctorsForAppointment([]);
      }
    }
  };

  const fetchDoctorDetails = async (doctorId) => {
    setSelectedDoctorDetails(null);
    try {
      const doctor = await getDoctorById(doctorId);
      setSelectedDoctorDetails(doctor);
    } catch (error) {
      console.error("Error fetching doctor details:", error);
    }
  };

  const fetchExistingAppointments = async (doctorId, appointmentDate) => {
    try {
      const appointments = await getAppointmentsByDoctorAndDate(
        doctorId,
        dayjs(appointmentDate).format("YYYY-MM-DD")
      );
      setExistingAppointments(Array.isArray(appointments) ? appointments : []);
    } catch (error) {
      console.error("Error fetching existing appointments:", error);
      setExistingAppointments([]);
    }
  };

  const handleBookAppointment = async () => {
    if (!patientDetails?.id) {
      toast.error("Patient information not found. Please contact support.");
      return;
    }

    if (!appointmentForm.departmentId || !appointmentForm.doctorId || !appointmentForm.appointmentDate || !appointmentForm.appointmentTime) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (!rescheduleTarget && !appointmentForm.paymentMethod) {
      toast.error("Please select a payment method.");
      return;
    }

    setSubmitting(true);
    try {
      // Parse time from format like "09:00 AM" to "HH:mm"
      const slotTime = dayjs(`${dayjs(appointmentForm.appointmentDate).format("YYYY-MM-DD")} ${appointmentForm.appointmentTime}`, "YYYY-MM-DD hh:mm A");
      const formattedTime = slotTime.format("HH:mm");
      const appointmentDateTime = `${dayjs(appointmentForm.appointmentDate).format("YYYY-MM-DD")}T${formattedTime}`;
      
      if (rescheduleTarget?.id) {
        const existingData = rescheduleTarget.appointmentData || {};

        await updateAppointment(
          rescheduleTarget.id,
          buildPatientAppointmentUpdatePayload({
            patientId: patientDetails.id,
            doctorId: appointmentForm.doctorId,
            appointmentDateTime,
            isVirtual: appointmentForm.isVirtual,
            existingData,
          })
        );

        toast.success(
          rescheduleTarget.rescheduleMode === "edit"
            ? "Appointment updated successfully."
            : "Appointment rescheduled successfully."
        );
        setRescheduleTarget(null);
        handleCloseModal();
        await fetchDashboardDataRef.current?.(true);
        return;
      }

      console.log("Creating appointment with data:", {
        patientId: patientDetails.id,
        doctorId: appointmentForm.doctorId,
        appointmentDateTime,
        isVirtual: appointmentForm.isVirtual,
        paymentMethod: appointmentForm.paymentMethod,
      });

      // Create appointment with payment status
      const selectedDepartment = departments.find(
        (dept) => String(dept?.id) === String(appointmentForm.departmentId)
      );
      const selectedDepartmentName =
        selectedDepartment?.name || selectedDepartment?.departmentName || null;

      const appointmentData = {
        patientId: patientDetails.id,
        doctorId: appointmentForm.doctorId,
        appointmentDateTime,
        isVirtual: appointmentForm.isVirtual,
        paymentMethod: appointmentForm.paymentMethod === "pay-at-hospital" ? "PAY_AT_HOSPITAL" : "PAID_ONLINE",
        status: appointmentForm.paymentMethod === "pay-at-hospital" ? "PENDING_CONFIRMATION" : "CONFIRMED",
      };

      const createdAppointment = await createPatientPortalAppointment(appointmentData);
      rememberInvoiceDepartment(
        getOrganizationId(user),
        {
          patientId: patientDetails.id,
          appointmentId: createdAppointment?.id || createdAppointment?.appointmentId,
        },
        {
          departmentId: appointmentForm.departmentId,
          departmentName: selectedDepartmentName,
          doctorId: appointmentForm.doctorId,
        }
      );

      await syncPatientTypeForWorkflow(patientDetails.id, "OUT", { force: true });

      console.log("Created appointment response:", createdAppointment);

      // Schedule reminders for all self-booked appointments, including pay-at-hospital
      if (createdAppointment?.id || createdAppointment?.appointmentId) {
        scheduleRemindersForAppointment(
          {
            ...createdAppointment,
            appointmentDateTime,
            patientId: patientDetails.id,
            doctorId: appointmentForm.doctorId,
            isVirtual: appointmentForm.isVirtual || false,
            virtualLink: createdAppointment?.virtualLink || createdAppointment?.virtual_link || createdAppointment?.virtualMeeting?.url || null,
          },
          {
            patient: patientDetails,
            doctor: appointmentForm.selectedDoctor,
          }
        ).catch((error) => {
          console.error("Failed to schedule appointment reminders:", error);
          // Don't block the flow if reminder scheduling fails
        });
      }

      // If online payment, create invoice and process payment
      if (appointmentForm.paymentMethod === "pay-online") {
        try {
          // Create invoice for appointment
          const appointmentAmount = 500; // Default appointment fee - should come from doctor/tariff
          const invoiceData = {
            patientId: patientDetails.id,
            doctorId:
              appointmentForm.doctorId != null && appointmentForm.doctorId !== ""
                ? Number(appointmentForm.doctorId)
                : undefined,
            departmentId:
              appointmentForm.departmentId != null &&
              appointmentForm.departmentId !== ""
                ? Number(appointmentForm.departmentId)
                : undefined,
            amount: appointmentAmount,
            sgst: appointmentAmount * 0.085, // 8.5% SGST
            cgst: appointmentAmount * 0.085, // 8.5% CGST
            totalAmount: appointmentAmount * 1.17, // Total after tax
            invoiceType: "OPD",
            date: dayjs().format("YYYY-MM-DDTHH:mm"),
            paymentMode: "ONLINE",
            billType: "OPD",
            appointmentId: createdAppointment.id,
            userId: user?.id || undefined,
          };

          const invoice = await createInvoice(invoiceData);
          rememberInvoiceDepartment(
            getOrganizationId(user),
            {
              patientId: patientDetails.id,
              appointmentId: createdAppointment?.id || createdAppointment?.appointmentId,
              invoiceId: invoice?.id,
            },
            {
              departmentId: appointmentForm.departmentId,
              departmentName: selectedDepartmentName,
              doctorId: appointmentForm.doctorId,
            }
          );
          
          // Generate token after payment
          if (invoice?.id) {
            toast.success("Payment processed successfully! Appointment confirmed.");
          }
        } catch (invoiceError) {
          console.error("Error creating invoice:", invoiceError);
          toast.error("Appointment created but payment processing failed. Please contact support.");
        }
      } else {
        // Pay at hospital - appointment pending confirmation
        toast.success("Appointment request submitted! Please visit the front desk for confirmation and payment.");
      }

      handleCloseModal();
      
      // Refresh appointments - force refresh to get latest data
      const [appointmentsRaw, invoicesRaw] = await Promise.all([
        getAppointmentsByPatientId(patientDetails.id).catch(() => []),
        getInvoicesByPatientId(patientDetails.id).catch(() => []),
      ]);
      const appointments = Array.isArray(appointmentsRaw) ? appointmentsRaw : appointmentsRaw ? [appointmentsRaw] : [];
      const invoicePaymentLookup = buildInvoicePaymentLookup(invoicesRaw);

      try {
        await autoCancelPastAppointments(appointments);
      } catch (e) {
        console.error("Failed to auto-cancel some no-show appointments", e);
      }

      const upcoming = [];
      const history = [];
      
      console.log("All appointments fetched:", appointments);
      
      appointments.forEach((appointment) => {
        const timestamp = appointment?.appointmentDateTime || appointment?.appointmentDate || appointment?.scheduledAt || appointment?.date;
        const schedule = dayjs(timestamp);
        const doctorId = appointment?.doctorId || appointment?.doctor?.id;
        const doctorInfo = doctors.find(d => d.id === doctorId) || appointment?.doctor || {};
        
        // Check if this is the appointment we just created (by matching doctorId and time)
        const appointmentTimeStr = appointment?.appointmentDateTime || appointment?.appointmentDate;
        const isJustCreated = createdAppointment?.id === appointment?.id ||
                             (appointmentForm.doctorId === doctorId && 
                              appointmentDateTime === appointmentTimeStr);
        
        // Check for virtual appointment flag in multiple possible field names
        // If we just created it as virtual, mark it as virtual even if backend doesn't return the flag yet
        const isVirtual = appointment?.isVirtual === true || 
                         appointment?.isVirtual === 1 ||
                         appointment?.is_virtual === true ||
                         appointment?.is_virtual === 1 ||
                         appointment?.virtual === true ||
                         appointment?.virtual === 1 ||
                         appointment?.appointmentType === 'virtual' ||
                         appointment?.type === 'virtual' ||
                         String(appointment?.appointmentType || '').toLowerCase() === 'virtual' ||
                         String(appointment?.type || '').toLowerCase() === 'virtual' ||
                         (isJustCreated && appointmentForm.isVirtual) || // If we just created it as virtual, mark it as virtual
                         false;
        
        // Check for video link in multiple possible field names
        const videoLink = appointment?.virtualMeeting?.url ||
                         appointment?.virtualMeeting?.link ||
                         appointment?.videoLink || 
                         appointment?.video_link || 
                         appointment?.meetingLink || 
                         appointment?.meeting_link ||
                         appointment?.virtualLink || 
                         appointment?.virtual_link ||
                         appointment?.videoUrl ||
                         appointment?.video_url ||
                         null;

        const enriched = {
          id: appointment?.id || `${timestamp}-${doctorId}`,
          when: schedule,
          rawDate: timestamp,
          doctorName: appointment?.doctorName || doctorInfo?.name || "Assigned Doctor",
          departmentName: doctorInfo?.departmentName || appointment?.departmentName || "Multi Speciality",
          status: isAppointmentCancelled(appointment) ? "Cancelled" : (appointment?.status || "Scheduled"),
          location: appointment?.location || doctorInfo?.department || "",
          isVirtual: isVirtual,
          videoLink: videoLink,
          // Store full appointment data for cancellation
          appointmentData: appointment,
          patientId: appointment?.patientId,
          doctorId: appointment?.doctorId || doctorId,
          paymentMethod:
            appointment?.paymentMethod || appointment?.payment_method,
          paymentMode:
            getAppointmentInvoicePaymentMode(appointment?.id, invoicePaymentLookup) ||
            appointment?.paymentMode ||
            appointment?.payment_mode,
        };
        
        // Debug log for virtual appointments
        if (isVirtual) {
          console.log("Enriched virtual appointment:", enriched);
        }
        
        if (isClosedPatientAppointment(appointment)) {
          history.push(enriched);
        } else {
          upcoming.push(enriched);
        }
      });
      
      upcoming.sort((a, b) => a.when.valueOf() - b.when.valueOf());
      history.sort((a, b) => b.when.valueOf() - a.when.valueOf());
      
      setUpcomingAppointments(upcoming);
      setPastAppointments(history);
    } catch (err) {
      console.error("Error booking appointment:", err);
      toast.error(err?.response?.data?.message || "Failed to book appointment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredDoctors = useMemo(() => {
    let filtered = doctors;
    
    if (searchDoctorQuery) {
      const query = searchDoctorQuery.toLowerCase();
      filtered = filtered.filter(
        (doctor) =>
          doctor.name?.toLowerCase().includes(query) ||
          doctor.departmentName?.toLowerCase().includes(query) ||
          doctor.specialty?.toLowerCase().includes(query)
      );
    }
    
    if (selectedDepartment) {
      // Normalize selectedDepartment to string for consistent comparison
      const selectedDeptId = String(selectedDepartment);
      
      filtered = filtered.filter((doctor) => {
        // Check single departmentId (normalize both sides)
        if (doctor.departmentId != null) {
          if (String(doctor.departmentId) === selectedDeptId) {
            return true;
          }
        }
        
        // Check departmentIds array
        if (Array.isArray(doctor.departmentIds) && doctor.departmentIds.length > 0) {
          const matches = doctor.departmentIds.some(
            (id) => id != null && String(id) === selectedDeptId
          );
          if (matches) return true;
        }
        
        // Check departmentName match (fallback)
        const selectedDept = departments.find(
          (d) => d.id != null && String(d.id) === selectedDeptId
        );
        if (selectedDept) {
          const deptName = (selectedDept.name || selectedDept.departmentName || "").toLowerCase();
          const doctorDeptName = (doctor.departmentName || "").toLowerCase();
          if (doctorDeptName === deptName) {
            return true;
          }
        }
        
        return false;
      });
    }
    
    return filtered;
  }, [doctors, searchDoctorQuery, selectedDepartment, departments]);

  // Helper to resolve department name for a doctor
  const getDoctorDepartmentName = useMemo(() => {
    // Create a map of doctor IDs to resolved department names
    const departmentNameMap = new Map();
    
    doctors.forEach((doctor) => {
      let departmentName = doctor.departmentName;
      
      // If departmentName is missing, try to resolve it from departments array
      if (!departmentName || departmentName.trim() === '' || departmentName === 'N/A') {
        // Try to get department from departmentId
        if (doctor.departmentId != null) {
          const dept = departments.find(d => d.id != null && String(d.id) === String(doctor.departmentId));
          if (dept) {
            departmentName = dept.name || dept.departmentName || null;
          }
        }
        
        // If still not found, try departmentIds array (use first one)
        if (!departmentName && Array.isArray(doctor.departmentIds) && doctor.departmentIds.length > 0) {
          const firstDeptId = doctor.departmentIds[0];
          if (firstDeptId != null) {
            const dept = departments.find(d => d.id != null && String(d.id) === String(firstDeptId));
            if (dept) {
              departmentName = dept.name || dept.departmentName || null;
            }
          }
        }
        
        // Fallback to doctor.department if it's a string
        if (!departmentName && doctor.department && typeof doctor.department === 'string') {
          departmentName = doctor.department;
        }
      }
      
      departmentNameMap.set(doctor.id, departmentName || 'N/A');
    });
    
    return (doctorId) => departmentNameMap.get(doctorId) || 'N/A';
  }, [doctors, departments]);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const openRescheduleModal = async (appointment, mode = "reschedule") => {
    const data = appointment?.appointmentData || {};
    const doctorId = appointment?.doctorId || data?.doctorId;
    let doctor = doctors.find((d) => Number(d.id) === Number(doctorId));

    if (!doctor && doctorId) {
      try {
        doctor = await getDoctorById(doctorId);
      } catch (e) {
        console.error("Error loading doctor for reschedule:", e);
      }
    }

    const departmentId = doctor?.departmentId
      ? String(doctor.departmentId)
      : "";

    if (departmentId) {
      await fetchDoctorsByDepartment(departmentId);
    }

    const paymentRaw = data?.paymentMethod || data?.payment_method || appointment?.paymentMethod;
    const paymentMethod =
      String(paymentRaw || "").toUpperCase() === "PAID_ONLINE" || appointment.isVirtual
        ? "pay-online"
        : "pay-at-hospital";

    setRescheduleTarget({
      ...appointment,
      rescheduleMode: appointment.isVirtual ? "edit" : mode,
    });
    setAppointmentForm({
      departmentId,
      doctorId: doctorId || "",
      appointmentDate: appointment.when?.isValid?.() ? appointment.when : null,
      appointmentTime: appointment.when?.isValid?.()
        ? appointment.when.format("hh:mm A")
        : "",
      isVirtual: Boolean(appointment.isVirtual),
      paymentMethod,
      selectedDoctor: doctor || null,
    });

    if (doctorId) {
      await fetchDoctorDetails(doctorId);
    }
    if (doctorId && appointment.when?.isValid?.()) {
      await fetchExistingAppointments(doctorId, appointment.when);
    }

    setActiveQuickAction(
      QUICK_ACTIONS.find((a) =>
        appointment.isVirtual ? a.key === "virtual-consult" : a.key === "book-appointment"
      )
    );
  };

  const handleViewAppointment = async (appointment) => {
    if (!appointment) return;
    const matched = matchPrescriptionsToAppointment(
      patientPrescriptions,
      appointment
    );
    await openOpdPrintPreview({
      prescription: matched[0] || null,
      appointment,
      title: matched[0] ? "Prescription" : "Appointment",
    });
  };

  const handleEditAppointment = (appointment) => {
    openRescheduleModal(appointment, "edit");
  };

  const renderAppointmentActionButton = (label, onClick) => (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      onClick={onClick}
      disableRipple={!onClick}
      sx={{
        height: 18,
        fontSize: "0.55rem",
        textTransform: "none",
        fontWeight: 600,
        py: 0,
        px: 0.5,
        minWidth: "auto",
        lineHeight: 1.2,
        borderColor: theme.palette.grey[400],
        color: theme.palette.text.secondary,
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick
          ? {
              borderColor: theme.palette.grey[600],
              backgroundColor: `${theme.palette.grey[100]}40`,
              color: theme.palette.text.primary,
            }
          : {
              borderColor: theme.palette.grey[400],
              backgroundColor: "transparent",
              color: theme.palette.text.secondary,
            },
      }}
    >
      {label}
    </Button>
  );

  const handleCancelAppointment = async (appointment) => {
    if (!appointment?.id || !appointment?.appointmentData) {
      toast.error("Appointment information not found.");
      return;
    }

    const appointmentData = appointment.appointmentData;

    if (isAppointmentCancelled(appointmentData)) {
      toast.info("This appointment is already cancelled.");
      return;
    }

    if (isAppointmentCompleted(appointmentData)) {
      toast.info("Cannot cancel a completed appointment.");
      return;
    }

    const confirmed = await confirmAsync(
      `Are you sure you want to cancel your appointment with ${appointment.doctorName} on ${appointment.when?.format("DD MMM YYYY, hh:mm A")}?`,
      { title: "Cancel appointment", tone: "warning", confirmLabel: "Cancel appointment", cancelLabel: "Keep" }
    );
    if (!confirmed) {
      return;
    }

    try {
      await cancelAppointmentSoft(
        { ...appointmentData, id: appointment.id },
        { cancelReminders: true }
      );

      toast.success("Appointment cancelled successfully.");
      await fetchDashboardDataRef.current?.(true);
    } catch (error) {
      console.error("Error cancelling appointment:", error);
      if (error?.code === "ALREADY_CANCELLED" || error?.code === "ALREADY_COMPLETED") {
        toast.info(error.message);
        return;
      }
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to cancel appointment. Please try again."
      );
    }
  };

  const handleSpecialtyClick = async (specialtyLabel) => {
    // Load departments and doctors if not already loaded
    if (departments.length === 0 || doctors.length === 0) {
      try {
        const [doctorsData, departmentsData] = await Promise.all([
          getAllDoctors({ forceRefresh: false }).catch(() => []),
          getAllDepartment().catch(() => []),
        ]);
        setDoctors(Array.isArray(doctorsData) ? doctorsData.filter(d => d.is_active !== 0) : []);
        setDepartments(Array.isArray(departmentsData) ? departmentsData : []);
      } catch (err) {
        console.error("Error loading data for specialty:", err);
        toast.error("Failed to load department data. Please try again.");
        return;
      }
    }

    // Try to find matching department by name
    // Map specialty labels to common department name variations
    const specialtyToDepartmentMap = {
      "Cardiac Care": ["Cardiology", "Cardiac", "Heart", "Cardiac Care"],
      "Cancer Care": ["Oncology", "Cancer", "Cancer Care", "Oncologist"],
      "Neurosciences": ["Neurology", "Neurosciences", "Neuroscience", "Neuro"],
      "Gastro Sciences": ["Gastroenterology", "Gastro", "Gastro Sciences", "Gastroenterologist"],
      "Orthopaedics": ["Orthopedics", "Orthopaedics", "Orthopedic", "Orthopaedic"],
      "Renal Care": ["Nephrology", "Renal", "Renal Care", "Kidney"],
    };

    const possibleNames = specialtyToDepartmentMap[specialtyLabel] || [specialtyLabel];
    
    // Find matching department
    const matchingDept = departments.find((dept) => {
      const deptName = (dept.name || dept.departmentName || "").toLowerCase();
      return possibleNames.some((name) => 
        deptName.includes(name.toLowerCase()) || 
        name.toLowerCase().includes(deptName)
      );
    });

    if (matchingDept) {
      // Open search doctor modal with department pre-selected
      setSelectedDepartment(String(matchingDept.id));
      setSearchDoctorQuery("");
      setActiveQuickAction(QUICK_ACTIONS.find(a => a.key === "search-doctor"));
    } else {
      // If no exact match, still open the modal but show all doctors
      // and show a message
      setSelectedDepartment("");
      setSearchDoctorQuery(specialtyLabel);
      setActiveQuickAction(QUICK_ACTIONS.find(a => a.key === "search-doctor"));
      toast.info(`Showing all doctors. Use the department filter to narrow down results for ${specialtyLabel}.`);
    }
  };

  const DASHBOARD_TABLE_PAGE_SIZE = 6;
  const DASHBOARD_STATUS_FILTERS = [
    { id: "all", label: "All" },
    { id: "completed", label: "Completed" },
    { id: "scheduled", label: "Schedule" },
    { id: "cancelled", label: "Cancelled" },
  ];
  const CONSULTATION_STATUS_FILTERS = DASHBOARD_STATUS_FILTERS.filter((option) => option.id !== "scheduled");
  const dashboardTableCardSx = {
    p: 1,
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    height: 400,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxSizing: "border-box",
  };
  const dashboardTableTitleSx = {
    fontSize: "0.75rem",
    fontWeight: 700,
    background: "linear-gradient(135deg, #0B78D0 0%, #0866B3 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  };
  const matchesDashboardStatusFilter = (status, filter) => {
    if (!filter || filter === "all") return true;
    const value = String(status || "").trim().toLowerCase();
    if (filter === "completed") return value === "completed";
    if (filter === "scheduled" || filter === "booked") {
      const isScheduled =
        value === "scheduled" || value === "schedule" || value === "confirmed" || value === "pending confirmation";
      if (filter === "scheduled") return isScheduled;
      return value === "completed" || isScheduled;
    }
    if (filter === "cancelled") return value === "cancelled" || value === "canceled" || value === "rejected";
    return true;
  };
  const pageOfRows = (rows, page, setPage) => {
    const maxPage = Math.max(0, Math.ceil(rows.length / DASHBOARD_TABLE_PAGE_SIZE) - 1);
    if (page > maxPage) setPage(maxPage);
    const safePage = Math.min(page, maxPage);
    return {
      safePage,
      visibleRows: rows.slice(
        safePage * DASHBOARD_TABLE_PAGE_SIZE,
        safePage * DASHBOARD_TABLE_PAGE_SIZE + DASHBOARD_TABLE_PAGE_SIZE
      ),
    };
  };
  const renderDashboardPagination = (count, page, setPage) => (
    <TablePagination
      component="div"
      count={count}
      page={page}
      onPageChange={(_, nextPage) => setPage(nextPage)}
      rowsPerPage={DASHBOARD_TABLE_PAGE_SIZE}
      rowsPerPageOptions={[DASHBOARD_TABLE_PAGE_SIZE]}
      labelRowsPerPage=""
      sx={{
        mt: "auto",
        borderTop: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        "& .MuiTablePagination-toolbar": { minHeight: 36, pl: 1, pr: 0.5 },
        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-input, & .MuiTablePagination-select": {
          display: "none",
        },
        "& .MuiTablePagination-displayedRows": { fontSize: "0.7rem", m: 0 },
      }}
    />
  );
  const renderStatusFilterButton = (onOpen) => (
    <IconButton size="small" onClick={(event) => onOpen(event.currentTarget)} aria-label={t("Filter")} sx={{ p: 0.25 }}>
      <CheckCircleIcon sx={{ fontSize: 18, color: "#0B78D0" }} />
    </IconButton>
  );
  const renderStatusFilterMenu = (anchor, onClose, value, onChange, options = DASHBOARD_STATUS_FILTERS) => (
    <Menu
      anchorEl={anchor}
      open={Boolean(anchor)}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      {options.map((option) => (
        <MenuItem
          key={option.id}
          selected={
            value === option.id ||
            (value === "booked" && (option.id === "completed" || option.id === "scheduled"))
          }
          onClick={() => onChange(option.id)}
          sx={{ fontSize: "0.8rem", minHeight: 32, gap: 1 }}
        >
          <CheckIcon
            sx={{
              fontSize: 16,
              visibility:
                value === option.id ||
                (value === "booked" && (option.id === "completed" || option.id === "scheduled"))
                  ? "visible"
                  : "hidden",
            }}
          />
          {t(option.label)}
        </MenuItem>
      ))}
    </Menu>
  );

  const renderAppointments = (items, emptyMessage) => {
    const { safePage, visibleRows } = pageOfRows(items, upcomingPage, setUpcomingPage);
    return (
    <Box sx={dashboardTableCardSx}>
      <Box sx={{ display: "flex", alignItems: "center", minHeight: 28 }}>
        <Typography variant="subtitle2" sx={dashboardTableTitleSx}>
          {emptyMessage.title}
        </Typography>
      </Box>
      <Divider sx={{ my: 0.75 }} />
      {items.length === 0 ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary", textAlign: "center", px: 1 }}>
          <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>{t("No records yet.")}</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={dashboardTableHeaderCellSx}>{t("Date")}</TableCell>
                <TableCell sx={dashboardTableHeaderCellSx}>{t("Dr Name")}</TableCell>
                <TableCell sx={dashboardTableHeaderCellSx}>{t("Department")}</TableCell>
                <TableCell sx={dashboardTableHeaderCellSx}>{t("Status")}</TableCell>
                <TableCell sx={dashboardTableHeaderCellSx} align="right">{t("Action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((appointment) => {
                const statusLabel = getDisplayAppointmentStatus(appointment);
                const statusColor =
                  statusLabel === "Pending Confirmation"
                    ? "warning"
                    : statusLabel === "CONFIRMED" || statusLabel === "Confirmed"
                      ? "success"
                      : "primary";
                return (
                  <TableRow key={`${appointment.id}-${appointment.rawDate}`} hover>
                    <TableCell sx={dashboardTableBodyCellSx}>
                      {appointment.when?.isValid?.()
                        ? appointment.when.format("DD MMM YYYY • hh:mm A")
                        : "—"}
                    </TableCell>
                    <TableCell sx={dashboardTableBodyCellSx}>{appointment.doctorName || "—"}</TableCell>
                    <TableCell sx={dashboardTableBodyCellSx}>{appointment.departmentName || "—"}</TableCell>
                    <TableCell sx={dashboardTableBodyCellSx}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {appointment.isVirtual && (
                          <Chip
                            icon={<VideoCallIcon sx={{ fontSize: 10 }} />}
                            label={t("Virtual")}
                            color="secondary"
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: "0.6rem", "& .MuiChip-label": { px: 0.5 }, "& .MuiChip-icon": { ml: 0.4 } }}
                          />
                        )}
                        <Chip
                          size="small"
                          color={statusColor}
                          variant="outlined"
                          label={t(statusLabel)}
                          sx={{ height: 18, fontSize: "0.6rem", "& .MuiChip-label": { px: 0.5 } }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={dashboardTableBodyCellSx} align="right">
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "flex-end" }}>
                        {renderAppointmentActionButton(t("View"), () => handleViewAppointment(appointment))}
                        {canPatientRescheduleAppointment(appointment) &&
                          renderAppointmentActionButton(t("Edit"), () => handleEditAppointment(appointment))}
                        {canPatientCancelAppointment(appointment) &&
                          renderAppointmentActionButton(t("Cancel"), () => handleCancelAppointment(appointment))}
                        {appointment.isVirtual && appointment.videoLink && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<LinkIcon sx={{ fontSize: 10 }} />}
                            onClick={() => window.open(appointment.videoLink, "_blank")}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.55rem",
                              fontWeight: 600,
                              py: 0,
                              px: 0.75,
                              height: 18,
                              minWidth: "auto",
                              lineHeight: 1.2,
                              backgroundColor: theme.palette.primary.main,
                              "&:hover": { backgroundColor: theme.palette.primary.dark },
                              "& .MuiButton-startIcon": { mr: 0.4 },
                            }}
                          >
                            {t("Join")}
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {renderDashboardPagination(items.length, safePage, setUpcomingPage)}
    </Box>
    );
  };

  const dashboardTableHeaderCellSx = {
    fontWeight: 700,
    fontSize: "0.7rem",
    py: 0.75,
    px: 1,
    whiteSpace: "nowrap",
    backgroundColor: "#F5F7FA",
  };
  const dashboardTableBodyCellSx = {
    fontSize: "0.7rem",
    py: 0.75,
    px: 1,
  };

  const renderPreviousConsultations = () => {
    const linkedAppointmentIds = new Set(
      patientPrescriptions
        .map((prescription) => prescription?.appointmentId ?? prescription?.appointment_id)
        .filter((id) => id != null && id !== "")
        .map((id) => String(id))
    );
    const consultationRows = [
      ...patientPrescriptions.map((prescription) => ({
        key: `rx-${getPrescriptionDisplayId(prescription)}`,
        sortValue: dayjs(prescription?.createdAt || prescription?.prescriptionDate || 0).valueOf(),
        dateLabel: getPrescriptionDisplayDate(prescription),
        prescriptionId: getPrescriptionDisplayId(prescription),
        doctorName: getPrescriptionDoctorName(prescription),
        complaints: getShortComplaintsText(prescription),
        status: "Completed",
        reason: "—",
        prescription,
        appointment: findAppointmentForPrescription(prescription),
        onView: () => handleViewPrescription(prescription),
      })),
      ...pastAppointments
        .filter((appointment) => {
          const appointmentId =
            appointment?.appointmentData?.id ||
            appointment?.appointmentData?.appointmentId ||
            appointment?.id;
          const data = appointment?.appointmentData || appointment;
          const closedWithoutVisit =
            isAppointmentCancelled(data) || isAppointmentRejected(data);
          if (closedWithoutVisit) return true;
          return appointmentId == null || !linkedAppointmentIds.has(String(appointmentId));
        })
        .map((appointment) => {
          const data = appointment?.appointmentData || appointment;
          const cancelled = isAppointmentCancelled(data) || isAppointmentRejected(data);
          return {
            key: `appointment-${appointment.id}-${appointment.rawDate}`,
            sortValue: appointment.when?.valueOf?.() || 0,
            dateLabel: appointment.when?.isValid?.() ? appointment.when.format("DD MMM YYYY") : "—",
            prescriptionId: "—",
            doctorName: appointment.doctorName || "—",
            complaints: "—",
            status: isAppointmentRejected(data)
              ? "Rejected"
              : cancelled
                ? "Cancelled"
                : "Completed",
            reason: cancelled ? getAppointmentCancelReasonText(data) : "—",
            prescription: matchPrescriptionsToAppointment(patientPrescriptions, appointment)[0] || null,
            appointment,
            onView: () => handleViewAppointment(appointment),
          };
        }),
    ].sort((a, b) => b.sortValue - a.sortValue);
    const filteredConsultationRows = consultationRows.filter((row) =>
      matchesDashboardStatusFilter(row.status, consultationStatusFilter)
    );
    const { safePage, visibleRows } = pageOfRows(
      filteredConsultationRows,
      consultationPage,
      setConsultationPage
    );

    return (
    <Box sx={dashboardTableCardSx}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minHeight: 28 }}>
        <Typography variant="subtitle2" sx={dashboardTableTitleSx}>
          {t("Previous Consultations")}
        </Typography>
        {renderStatusFilterButton(setConsultationFilterAnchor)}
      </Box>
      {renderStatusFilterMenu(
        consultationFilterAnchor,
        () => setConsultationFilterAnchor(null),
        consultationStatusFilter,
        (nextFilter) => {
          setConsultationStatusFilter(nextFilter);
          setConsultationPage(0);
          setConsultationFilterAnchor(null);
        },
        CONSULTATION_STATUS_FILTERS
      )}
      <Divider sx={{ my: 0.75 }} />
      {filteredConsultationRows.length === 0 ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary", textAlign: "center", px: 1 }}>
          <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>{t("No records yet.")}</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={dashboardTableHeaderCellSx}>{t("Date")}</TableCell>
                <TableCell sx={dashboardTableHeaderCellSx}>{t("Prescription ID")}</TableCell>
                <TableCell sx={dashboardTableHeaderCellSx}>{t("Dr Name")}</TableCell>
                <TableCell sx={dashboardTableHeaderCellSx}>{t("Complaints")}</TableCell>
                <TableCell sx={dashboardTableHeaderCellSx}>{t("Status")}</TableCell>
                <TableCell sx={dashboardTableHeaderCellSx}>{t("Reason")}</TableCell>
                <TableCell sx={dashboardTableHeaderCellSx} align="right">{t("Action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => (
                <TableRow key={row.key} hover>
                  <TableCell sx={dashboardTableBodyCellSx}>{row.dateLabel}</TableCell>
                  <TableCell sx={dashboardTableBodyCellSx}>{row.prescriptionId}</TableCell>
                  <TableCell sx={dashboardTableBodyCellSx}>{row.doctorName}</TableCell>
                  <TableCell sx={{ ...dashboardTableBodyCellSx, maxWidth: 180 }}>{row.complaints}</TableCell>
                  <TableCell sx={dashboardTableBodyCellSx}>
                    <Chip
                      size="small"
                      label={t(row.status)}
                      color={row.status === "Completed" ? "success" : "error"}
                      variant="outlined"
                      sx={{ height: 18, fontSize: "0.6rem", "& .MuiChip-label": { px: 0.5 } }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...dashboardTableBodyCellSx, maxWidth: 160 }}>
                    {row.reason === "—" ? "—" : t(row.reason)}
                  </TableCell>
                  <TableCell sx={dashboardTableBodyCellSx} align="right">
                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
                      {row.status === "Completed" &&
                        renderVisitPrintMenu({
                          prescription: row.prescription,
                          appointment: row.appointment,
                          iconOnly: true,
                        })}
                      {renderAppointmentActionButton(t("View"), row.onView)}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {renderDashboardPagination(filteredConsultationRows.length, safePage, setConsultationPage)}
    </Box>
    );
  };

  const renderBookedTestsTable = () => {
    const filteredLabTests = dashboardLabTests.filter((test) => {
      const rawStatus = String(test?.status || test?.resultStatus || "").toUpperCase();
      const statusLabel =
        rawStatus.includes("CANCEL") || rawStatus.includes("REJECT")
          ? "Cancelled"
          : getLabTestListStatusLabel(test);
      return matchesDashboardStatusFilter(statusLabel, bookedTestStatusFilter);
    });
    const { safePage, visibleRows } = pageOfRows(filteredLabTests, bookedTestPage, setBookedTestPage);

    return (
    <Box id="patient-booked-tests" sx={dashboardTableCardSx}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minHeight: 28 }}>
        <Typography variant="subtitle2" sx={dashboardTableTitleSx}>
          {t("Booked Tests")}
        </Typography>
        {renderStatusFilterButton(setBookedTestFilterAnchor)}
      </Box>
      {renderStatusFilterMenu(
        bookedTestFilterAnchor,
        () => setBookedTestFilterAnchor(null),
        bookedTestStatusFilter,
        (nextFilter) => {
          setBookedTestStatusFilter(nextFilter);
          setBookedTestPage(0);
          setBookedTestFilterAnchor(null);
        }
      )}
      <Divider sx={{ my: 0.75 }} />
      {filteredLabTests.length === 0 ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary", textAlign: "center", px: 1 }}>
          <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>{t("No records yet.")}</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={dashboardTableHeaderCellSx}>{t("Date")}</TableCell>
                <TableCell sx={dashboardTableHeaderCellSx}>{t("Test")}</TableCell>
                <TableCell sx={dashboardTableHeaderCellSx}>{t("Prescription ID")}</TableCell>
                <TableCell sx={dashboardTableHeaderCellSx}>{t("Status")}</TableCell>
                <TableCell sx={dashboardTableHeaderCellSx} align="right">{t("Action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((test) => {
                const rawStatus = String(test?.status || test?.resultStatus || "").toUpperCase();
                const statusLabel =
                  rawStatus.includes("CANCEL") || rawStatus.includes("REJECT")
                    ? "Cancelled"
                    : getLabTestListStatusLabel(test);
                return (
                  <TableRow key={test.id || test.reportId || test.bookingId} hover>
                    <TableCell sx={dashboardTableBodyCellSx}>
                      {test.date?.isValid?.()
                        ? `${test.date.format("DD MMM YYYY")}${test.preferredTime ? ` • ${test.preferredTime}` : ""}`
                        : "—"}
                    </TableCell>
                    <TableCell sx={dashboardTableBodyCellSx}>{test.testName}</TableCell>
                    <TableCell sx={dashboardTableBodyCellSx}>{test.prescriptionId || "—"}</TableCell>
                    <TableCell sx={dashboardTableBodyCellSx}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={t(statusLabel)}
                          color={statusLabel === "Completed" ? "success" : statusLabel === "Cancelled" ? "error" : "warning"}
                          variant="outlined"
                          sx={{ height: 18, fontSize: "0.6rem", "& .MuiChip-label": { px: 0.5 } }}
                        />
                        {test.bookedByPatient && (
                          <Chip
                            size="small"
                            label={t("Booked by patient")}
                            color="info"
                            variant="outlined"
                            sx={{ height: 18, fontSize: "0.6rem", "& .MuiChip-label": { px: 0.5 } }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={dashboardTableBodyCellSx} align="right">
                      {renderAppointmentActionButton(t("View"), () => handleViewLabReport(test))}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {renderDashboardPagination(filteredLabTests.length, safePage, setBookedTestPage)}
    </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "70vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isPatientProfileIncomplete(patientDetails)) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "70vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#FFFFFF", color: "#2F3A4A" }}>
      <PatientPortalHeader
        language={language}
        t={(key) => patientPortalTranslate(language, key)}
        isAuthenticated
        onLanguageChange={changeLanguage}
        onLogout={handleLogout}
        onEditProfile={() => setOpenEditProfile(true)}
        onMyProfile={() => setOpenEditProfile(true)}
        onSearch={() => openBookingSearch("book-appointment")}
        onNavigateSection={(label) => {
          if (label === "Modules") {
            scrollToId("patient-quick-actions");
            return;
          }
          if (label === "About") {
            scrollToId("patient-portal-footer");
          }
        }}
      />
      <Box
        sx={{
          minHeight: "calc(100vh - 72px)",
          px: { xs: 3, md: 6, lg: 10 },
          py: { xs: 2, md: 2.5 },
          overflow: "auto",
          backgroundColor: "#F5F7FA",
          width: "100%",
          boxSizing: "border-box",
    backgroundImage: `
      radial-gradient(
        circle at top left,
        rgba(11, 120, 208, 0.08) 0%,
        transparent 35%
      ),
      linear-gradient(
        180deg,
        #FFFFFF 0%,
        #F5F7FA 100%
      )
    `,
  }}
>
      <Box sx={{ width: "100%", maxWidth: "none", mx: "auto" }}>
        <Stack spacing={2} sx={{ pb: 1 }}>
        {error && (
          <Alert severity="error" sx={{ borderRadius: 1, py: 0.5 }}>
            {error}
          </Alert>
        )}

        {/* Hero Section with Search */}
        <Box sx={{ width: "100%", minWidth: 0 }}>
          {/* Search Bar - Prominent */}
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, md: 2 },
                borderRadius: "12px",
                border: "1px solid #E5E9EF",
                backgroundColor: "#FFFFFF",
                boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", md: "center" },
                  justifyContent: "space-between",
                  gap: { xs: 1, md: 3 },
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "0.95rem", md: "1rem" },
                    fontWeight: 700,
                    color: "#2F3A4A",
                    whiteSpace: { md: "nowrap" },
                  }}
                >
                  {t("Welcome {name}, How can we help you today?", { name: patientName })}
                </Typography>
                {/* <Typography
                  sx={{
                    fontSize: { xs: "1rem", md: "1.15rem" },
                    fontWeight: 800,
                    color: "#2F3A4A",
                    textAlign: { xs: "left", md: "right" },
                  }}
                >
                  How can we help you today?
                </Typography> */}
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Quick Actions - Medanta Style */}
        <Box
          id="patient-quick-actions"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            justifyContent: "flex-start",
            width: "100%",
            boxSizing: "border-box",
            alignItems: "stretch",
          }}
        >
          {QUICK_ACTIONS.filter(action => !action.hidden).map((action) => {
            const isSelected = selectedQuickAction === action.key;
            const shouldHighlight = isSelected;
            
            return (
              <Paper
                key={action.key}
                elevation={0}
                onClick={() => handleQuickAction(action)}
                sx={{
                  borderRadius: 2,
                  p: 1.5,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1.5,
                  minHeight: 56,
                  minWidth: { xs: "calc(50% - 0.75rem)", sm: 0 },
                  flex: { xs: "1 1 calc(50% - 0.75rem)", sm: "1 1 0" },
                  backgroundColor: shouldHighlight
                    ? theme.palette.primary.main 
                    : theme.palette.background.paper,
                  color: shouldHighlight
                    ? theme.palette.common.white 
                    : theme.palette.text.primary,
                  border: shouldHighlight
                    ? "none" 
                    : `1.5px solid ${theme.palette.divider}`,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 12px rgba(0, 0, 0, 0.15)`,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    border: "none",
                    "& .action-icon-box": {
                      color: `${theme.palette.common.white} !important`,
                    },
                  },
                }}
              >
                <Box
                  className="action-icon-box"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: shouldHighlight
                      ? theme.palette.common.white 
                      : theme.palette.primary.main,
                    transition: "all 0.2s ease",
                  }}
                >
                  {React.cloneElement(action.icon, { 
                    sx: { fontSize: 24 } 
                  })}
                </Box>
                <Typography 
                  variant="body2" 
                  fontWeight={600}
                  sx={{ 
                    fontSize: "0.85rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t(action.title)}
                </Typography>
              </Paper>
            );
          })}
          <Paper
            elevation={0}
            onClick={() => {
              if (selectedQuickAction === "download-print") {
                setSelectedQuickAction("");
                return;
              }
              setSelectedQuickAction("download-print");
              setActiveQuickAction(null);
            }}
            sx={{
              borderRadius: 2,
              p: 1.5,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1.5,
              minHeight: 56,
              minWidth: { xs: "calc(50% - 0.75rem)", sm: 0 },
              flex: { xs: "1 1 calc(50% - 0.75rem)", sm: "1 1 0" },
              backgroundColor:
                selectedQuickAction === "download-print"
                  ? theme.palette.primary.main
                  : theme.palette.background.paper,
              color:
                selectedQuickAction === "download-print"
                  ? theme.palette.common.white
                  : theme.palette.text.primary,
              border:
                selectedQuickAction === "download-print"
                  ? "none"
                  : `1.5px solid ${theme.palette.divider}`,
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
                border: "none",
                "& .action-icon-box": {
                  color: `${theme.palette.common.white} !important`,
                },
              },
            }}
          >
            <Box
              className="action-icon-box"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color:
                  selectedQuickAction === "download-print"
                    ? theme.palette.common.white
                    : theme.palette.primary.main,
                transition: "all 0.2s ease",
              }}
            >
              <PrintIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}
            >
              {t("Download / Print")}
            </Typography>
          </Paper>
        </Box>

        {(selectedQuickAction === "book-appointment" ||
          selectedQuickAction === "virtual-consult" ||
          selectedQuickAction === "book-test") && (
          <Paper
            id="patient-booking-search"
            elevation={0}
            sx={{
              p: { xs: 1.5, md: 2 },
              borderRadius: "12px",
              border: "1px solid #E5E9EF",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            {selectedQuickAction === "virtual-consult" && (
              <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                {t("Virtual appointments require online payment only.")}
              </Alert>
            )}
            {selectedQuickAction === "book-test" && (
              <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                {t("Enter your prescription ID, select a doctor, then choose a slot.")}{" "}
                {t("The prescribing doctor and department are filled in from the prescription. Select the tests you want to book.")}{" "}
                {t("Completed tests do not need to be booked again. Select the pending tests you want.")}{" "}
                {t("A test can be booked only after 1 day has passed from when the doctor prescribed it.")}
              </Alert>
            )}
            <PatientDoctorSearchPanel
              key={selectedQuickAction}
              appointmentType={
                selectedQuickAction === "virtual-consult" ? "virtual" : "in-person"
              }
              requirePrescription={selectedQuickAction === "book-test"}
              patientId={patientDetails?.id}
              t={t}
            />
          </Paper>
        )}

        {selectedQuickAction === "download-print" && (
          <Paper
            id="patient-print-search"
            elevation={0}
            sx={{
              p: { xs: 1.5, md: 2 },
              borderRadius: "12px",
              border: "1px solid #E5E9EF",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              <TextField
                label={t("Prescription ID")}
                value={printPrescriptionIdInput}
                onChange={(event) => {
                  setPrintPrescriptionIdInput(event.target.value.replace(/\D/g, ""));
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    lookupPrescriptionForPrint(printPrescriptionIdInput, { notify: true });
                  }
                }}
                inputProps={{ inputMode: "numeric" }}
                size="small"
                sx={PRINT_SEARCH_FIELD_SX}
                InputProps={{
                  endAdornment: printLookupLoading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ) : null,
                }}
              />
              {printLookupPrescription &&
                renderVisitPrintMenu({
                  prescription: printLookupPrescription,
                  appointment: findAppointmentForPrescription(printLookupPrescription),
                  asField: true,
                  fieldLabel: t("Select document"),
                })}
              <TextField
                select
                label={t("Department")}
                value=""
                size="small"
                disabled
                sx={PRINT_SEARCH_FIELD_SX}
              >
                <MenuItem value="">{t("All departments")}</MenuItem>
              </TextField>
              <TextField
                select
                label={t("Available Doctor")}
                value=""
                size="small"
                disabled
                sx={PRINT_SEARCH_FIELD_SX}
              >
                <MenuItem value="">{t("Select doctor")}</MenuItem>
              </TextField>
              <TextField
                label={t("Search Doctor or Department")}
                value=""
                size="small"
                disabled
                sx={{ ...PRINT_SEARCH_FIELD_SX, flex: "1 1 240px" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#6B7280" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                disabled
                sx={{
                  height: 40,
                  minHeight: 40,
                  px: 3,
                  flex: { xs: "1 1 100%", sm: "0 0 120px" },
                  backgroundColor: "#0B78D0",
                  boxShadow: "none",
                  fontWeight: 700,
                  textTransform: "none",
                  "&.Mui-disabled": {
                    backgroundColor: "#0B78D0",
                    color: "#fff",
                    opacity: 0.7,
                  },
                }}
              >
                {t("Book")}
              </Button>
            </Box>
          </Paper>
        )}

        {/* Appointments Section */}
        <Box
          id="patient-appointments"
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
            width: "100%",
            alignItems: "stretch",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {renderAppointments(upcomingAppointments, {
              title: t("Upcoming Appointments"),
              subtitle: "",
            })}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            {renderPreviousConsultations()}
          </Box>
        </Box>

        {renderBookedTestsTable()}

      </Stack>

      {/* Search Doctor Modal */}
      {activeQuickAction?.key === "search-doctor" && (
        <Dialog
          open={true}
          onClose={handleCloseModal}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Search Doctor</Typography>
              <IconButton onClick={handleCloseModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                placeholder="Search by doctor name or department"
                value={searchDoctorQuery}
                onChange={(e) => setSearchDoctorQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Department</InputLabel>
                  <Select
                    value={selectedDepartment}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedDepartment(value === "" ? "" : String(value));
                    }}
                    label="Filter by Department"
                  >
                    <MenuItem value="">All Departments</MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={String(dept.id)}>
                        {dept.name || dept.departmentName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedDepartment && (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => setSelectedDepartment("")}
                    sx={{ 
                      mt: 1, 
                      minWidth: "auto", 
                      px: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor: theme.palette.primary.main,
                      "&:hover": {
                        borderColor: theme.palette.primary.dark,
                        backgroundColor: `${theme.palette.primary.main}10`,
                      },
                    }}
                    title="Clear filter"
                  >
                    Clear
                  </Button>
                )}
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Doctor Name</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDoctors.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No doctors found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredDoctors.map((doctor) => (
                        <TableRow key={doctor.id}>
                          <TableCell>{doctor.name}</TableCell>
                          <TableCell>{getDoctorDepartmentName(doctor.id)}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={() => {
                                handleCloseModal();
                                navigate(`/patient/doctor/${doctor.id}?type=in-person`);
                              }}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                boxShadow: `0 2px 4px ${theme.palette.primary.main}30`,
                                "&:hover": {
                                  boxShadow: `0 4px 8px ${theme.palette.primary.main}40`,
                                },
                              }}
                            >
                              Book Appointment
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button 
              onClick={handleCloseModal}
              variant="outlined"
              sx={{ 
                textTransform: "none",
                fontWeight: 600,
                borderColor: theme.palette.grey[400],
                color: theme.palette.text.secondary,
                "&:hover": {
                  borderColor: theme.palette.grey[600],
                  backgroundColor: `${theme.palette.grey[100]}40`,
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Book Appointment / Virtual Appointment Modal */}
      {rescheduleTarget &&
        (activeQuickAction?.key === "book-appointment" ||
          activeQuickAction?.key === "virtual-consult") && (
        <BasicModal
          open
          onClose={handleCloseModal}
          sx={LARGE_FORM_MODAL_SX}
          title={
            rescheduleTarget
              ? rescheduleTarget.rescheduleMode === "edit"
                ? "Edit Appointment"
                : "Reschedule Appointment"
              : activeQuickAction?.title
          }
        >
          <Box display="flex" flexDirection="column">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                {activeQuickAction?.key === "virtual-consult" && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Virtual appointments require online payment only.
                    </Alert>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    required
                    size="small"
                    label="Select Department"
                    value={appointmentForm.departmentId}
                    onChange={async (e) => {
                      const deptId = e.target.value;
                      setAppointmentForm({
                        ...appointmentForm,
                        departmentId: deptId,
                        doctorId: "",
                        appointmentDate: null,
                        appointmentTime: "",
                      });
                      setSelectedDoctorDetails(null);
                      setExistingAppointments([]);
                      if (deptId) {
                        await fetchDoctorsByDepartment(deptId);
                      } else {
                        setFilteredDoctorsForAppointment([]);
                      }
                    }}
                  >
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.name || dept.departmentName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    required
                    size="small"
                    label="Select Doctor"
                    value={appointmentForm.doctorId}
                    disabled={!appointmentForm.departmentId || filteredDoctorsForAppointment.length === 0}
                    onChange={async (e) => {
                      const doctorId = e.target.value;
                      if (rescheduleTarget) {
                        setAppointmentForm({
                          ...appointmentForm,
                          doctorId,
                          appointmentTime: "",
                        });
                        if (doctorId) {
                          await fetchDoctorDetails(doctorId);
                          if (appointmentForm.appointmentDate) {
                            await fetchExistingAppointments(doctorId, appointmentForm.appointmentDate);
                          }
                        } else {
                          setExistingAppointments([]);
                          setSelectedDoctorDetails(null);
                        }
                        return;
                      }
                      if (doctorId) {
                        const isVirtual = activeQuickAction?.key === "virtual-consult";
                        handleCloseModal();
                        navigate(`/patient/doctor/${doctorId}?type=${isVirtual ? "virtual" : "in-person"}`);
                      } else {
                        setAppointmentForm({
                          ...appointmentForm,
                          doctorId: "",
                          appointmentDate: null,
                          appointmentTime: "",
                        });
                        setExistingAppointments([]);
                        setSelectedDoctorDetails(null);
                      }
                    }}
                    helperText={
                      !appointmentForm.departmentId
                        ? "Please select a department first"
                        : filteredDoctorsForAppointment.length === 0
                          ? "No doctors available for this department"
                          : " "
                    }
                  >
                    {filteredDoctorsForAppointment.length === 0 ? (
                      <MenuItem value="" disabled>
                        {!appointmentForm.departmentId
                          ? "Please select a department first"
                          : "No doctors available for this department"}
                      </MenuItem>
                    ) : (
                      filteredDoctorsForAppointment.map((doctor) => (
                        <MenuItem key={doctor.id} value={doctor.id}>
                          {doctor.name}
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Appointment Date"
                    value={appointmentForm.appointmentDate}
                    onChange={async (newValue) => {
                      const doctorId = appointmentForm.doctorId;
                      setAppointmentForm({
                        ...appointmentForm,
                        appointmentDate: newValue,
                        appointmentTime: "",
                      });
                      if (doctorId && newValue) {
                        await fetchExistingAppointments(doctorId, newValue);
                      } else {
                        setExistingAppointments([]);
                      }
                    }}
                    minDate={dayjs()}
                    disabled={!appointmentForm.doctorId}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        size: "small",
                        helperText: !appointmentForm.doctorId ? "Please select a doctor first" : " ",
                      },
                    }}
                  />
                </Grid>

                {appointmentForm.doctorId && appointmentForm.appointmentDate && selectedDoctorDetails && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Available Time Slots
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {(() => {
                        const selectedDay = dayjs(appointmentForm.appointmentDate).format("dddd").toLowerCase();
                        const schedule = selectedDoctorDetails?.visitingSchedules?.[selectedDay];

                        if (!schedule || !Array.isArray(schedule.timings) || schedule.timings.length === 0) {
                          return (
                            <Typography variant="body2" color="text.secondary">
                              No available time slots for this day.
                            </Typography>
                          );
                        }

                        const selectedDate = dayjs(appointmentForm.appointmentDate);
                        const isToday = selectedDate.isSame(dayjs(), 'day');

                        return schedule.timings.map((time, index) => {
                          const slotTime = dayjs(`${selectedDate.format("YYYY-MM-DD")} ${time}`, "YYYY-MM-DD hh:mm A");
                          const isBooked = existingAppointments.some(appointment =>
                            dayjs(appointment.appointmentDateTime).format("HH:mm") === slotTime.format("HH:mm")
                          );
                          const isDisabled = isToday && slotTime.isBefore(dayjs());
                          const isSelected = appointmentForm.appointmentTime === time;

                          return (
                            <Button
                              key={index}
                              variant={isSelected ? "contained" : "outlined"}
                              color={isSelected ? "primary" : "primary"}
                              onClick={() => {
                                if (!isBooked && !isDisabled) {
                                  setAppointmentForm({ ...appointmentForm, appointmentTime: time });
                                }
                              }}
                              disabled={isBooked || isDisabled}
                              sx={{
                                minWidth: 80,
                                textTransform: "none",
                                fontWeight: 600,
                                backgroundColor: isDisabled 
                                  ? theme.palette.grey[300] 
                                  : isBooked 
                                    ? theme.palette.grey[400] 
                                    : isSelected 
                                      ? theme.palette.primary.main 
                                      : undefined,
                                color: isDisabled 
                                  ? theme.palette.grey[600] 
                                  : isBooked 
                                    ? theme.palette.common.white 
                                    : isSelected
                                      ? theme.palette.common.white
                                      : theme.palette.primary.main,
                                borderColor: isSelected ? theme.palette.primary.main : theme.palette.primary.main,
                                '&:hover': {
                                  backgroundColor: isDisabled 
                                    ? theme.palette.grey[300] 
                                    : isBooked 
                                      ? theme.palette.grey[400] 
                                      : isSelected
                                        ? theme.palette.primary.dark
                                        : `${theme.palette.primary.main}10`,
                                  borderColor: isSelected ? theme.palette.primary.dark : theme.palette.primary.dark,
                                },
                              }}
                            >
                              {time}
                            </Button>
                          );
                        });
                      })()}
                    </Box>
                    {appointmentForm.appointmentTime && (
                      <Typography variant="caption" color="success.main" sx={{ mt: 1, display: "block" }}>
                        Selected: {appointmentForm.appointmentTime}
                      </Typography>
                    )}
                  </Grid>
                )}

                {!rescheduleTarget && (
                  <Grid item xs={12}>
                    <FormLabel component="legend" sx={{ mb: 1, fontSize: "0.9rem", fontWeight: 600 }}>
                      Payment Method
                    </FormLabel>
                    <RadioGroup
                      row
                      value={appointmentForm.paymentMethod}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, paymentMethod: e.target.value })}
                    >
                      <FormControlLabel
                        value="pay-at-hospital"
                        control={<Radio size="small" disabled={appointmentForm.isVirtual} />}
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LocalHospitalIcon fontSize="small" />
                            <Typography variant="body2" sx={{ color: appointmentForm.isVirtual ? "text.disabled" : "inherit" }}>
                              Pay at Hospital
                            </Typography>
                          </Box>
                        }
                        disabled={appointmentForm.isVirtual}
                      />
                      <FormControlLabel
                        value="pay-online"
                        control={<Radio size="small" />}
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <PaymentIcon fontSize="small" />
                            <Typography variant="body2">Pay Here (Online/UPI)</Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                    {!appointmentForm.isVirtual && appointmentForm.paymentMethod === "pay-at-hospital" && (
                      <Alert severity="info" sx={{ mt: 1, py: 0.5, fontSize: "0.75rem" }}>
                        Your appointment will be confirmed after payment at the front desk.
                      </Alert>
                    )}
                    {!appointmentForm.isVirtual && appointmentForm.paymentMethod === "pay-online" && (
                      <Alert severity="info" sx={{ mt: 1, py: 0.5, fontSize: "0.75rem" }}>
                        Payment will be processed online and token will be generated immediately.
                      </Alert>
                    )}
                  </Grid>
                )}

                {appointmentForm.isVirtual && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      This is a virtual appointment. You will receive a video call link before the scheduled time.
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </LocalizationProvider>
            <FormModalActions
              onCancel={handleCloseModal}
              onSubmit={handleBookAppointment}
              submitLabel={
                rescheduleTarget
                  ? "Save Changes"
                  : appointmentForm.paymentMethod === "pay-online"
                    ? "Pay & Book Appointment"
                    : "Book Appointment"
              }
              submitDisabled={
                submitting ||
                !appointmentForm.departmentId ||
                !appointmentForm.doctorId ||
                !appointmentForm.appointmentDate ||
                !appointmentForm.appointmentTime ||
                (!rescheduleTarget && !appointmentForm.paymentMethod)
              }
              loading={submitting}
            />
          </Box>
        </BasicModal>
      )}

      {/* Second Opinion Modal */}
      {activeQuickAction?.key === "second-opinion" && (
        <Dialog
          open={true}
          onClose={handleCloseModal}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Second Opinion</Typography>
              <IconButton onClick={handleCloseModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Alert severity="info">
                Get expert medical advice from our top specialists. This feature allows you to consult with our doctors for a second opinion on your diagnosis or treatment plan.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Please contact our help desk or use the "Search Doctor" option to find a specialist for your second opinion consultation.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
            <Button 
              onClick={handleCloseModal}
              variant="outlined"
              sx={{ 
                textTransform: "none",
                fontWeight: 600,
                borderColor: theme.palette.grey[400],
                color: theme.palette.text.secondary,
                "&:hover": {
                  borderColor: theme.palette.grey[600],
                  backgroundColor: `${theme.palette.grey[100]}40`,
                },
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleCloseModal();
                // Open search doctor functionality via hero search
                // Open search doctor modal
                setTimeout(() => {
                  setActiveQuickAction(QUICK_ACTIONS.find(a => a.key === "search-doctor"));
                }, 100);
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                minWidth: 150,
                boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
                "&:hover": {
                  boxShadow: `0 4px 12px ${theme.palette.primary.main}60`,
                },
              }}
            >
              Search Doctor
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Health Checkup Modal */}
      {activeQuickAction?.key === "health-checkup" && (
        <Dialog
          open={true}
          onClose={handleCloseModal}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Get Health Checkup</Typography>
              <IconButton onClick={handleCloseModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Alert severity="info">
                Comprehensive health screening packages tailored to your needs. Choose from our range of health checkup packages.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Please contact our help desk or visit the hospital to book a health checkup package. You can also use "Book a Test" to schedule specific diagnostic tests.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
            <Button 
              onClick={handleCloseModal}
              variant="outlined"
              sx={{ 
                textTransform: "none",
                fontWeight: 600,
                borderColor: theme.palette.grey[400],
                color: theme.palette.text.secondary,
                "&:hover": {
                  borderColor: theme.palette.grey[600],
                  backgroundColor: `${theme.palette.grey[100]}40`,
                },
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleCloseModal();
                const action = QUICK_ACTIONS.find((a) => a.key === "book-test");
                if (action) handleQuickAction(action);
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                minWidth: 150,
                boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
                "&:hover": {
                  boxShadow: `0 4px 12px ${theme.palette.primary.main}60`,
                },
              }}
            >
              Book a Test
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit Profile Modal */}
      <BasicModal
        open={openEditProfile}
        onClose={handleCloseEditProfile}
        sx={LARGE_FORM_MODAL_SX}
        title="Edit Profile"
      >
        <Box component="form" onSubmit={profileFormik.handleSubmit} display="flex" flexDirection="column">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      id="name"
                      name="name"
                      label="Full Name"
                      value={profileFormik.values.name}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}
                      error={profileFormik.touched.name && Boolean(profileFormik.errors.name)}
                      helperText={profileFormik.touched.name && profileFormik.errors.name}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      id="mobile"
                      name="mobile"
                      label="Mobile Number"
                      value={profileFormik.values.mobile}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}
                      error={profileFormik.touched.mobile && Boolean(profileFormik.errors.mobile)}
                      helperText={profileFormik.touched.mobile && profileFormik.errors.mobile}
                      inputProps={{ maxLength: 10 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      id="email"
                      name="email"
                      label="Email"
                      type="email"
                      value={profileFormik.values.email}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}
                      error={profileFormik.touched.email && Boolean(profileFormik.errors.email)}
                      helperText={profileFormik.touched.email && profileFormik.errors.email}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      required
                      size="small"
                      id="gender"
                      name="gender"
                      label="Gender"
                      value={profileFormik.values.gender}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}
                      error={profileFormik.touched.gender && Boolean(profileFormik.errors.gender)}
                      helperText={profileFormik.touched.gender && profileFormik.errors.gender}
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Date of Birth"
                      value={profileFormik.values.dob}
                      onChange={(value) => profileFormik.setFieldValue("dob", value)}
                      maxDate={dayjs()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          size: "small",
                          error: profileFormik.touched.dob && Boolean(profileFormik.errors.dob),
                          helperText: profileFormik.touched.dob && profileFormik.errors.dob,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      id="aadhar"
                      name="aadhar"
                      label="Aadhar Number"
                      value={profileFormik.values.aadhar}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}
                      error={profileFormik.touched.aadhar && Boolean(profileFormik.errors.aadhar)}
                      helperText={profileFormik.touched.aadhar && profileFormik.errors.aadhar}
                      inputProps={{ maxLength: 12 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      id="address"
                      name="address"
                      label="Address"
                      multiline
                      minRows={2}
                      value={profileFormik.values.address}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}
                      error={profileFormik.touched.address && Boolean(profileFormik.errors.address)}
                      helperText={profileFormik.touched.address && profileFormik.errors.address}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider>
                      <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                        Change Password (Optional)
                      </Typography>
                    </Divider>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      id="currentPassword"
                      name="currentPassword"
                      label="Current Password"
                      type={showCurrentPassword ? "text" : "password"}
                      value={profileFormik.values.currentPassword}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}
                      error={profileFormik.touched.currentPassword && Boolean(profileFormik.errors.currentPassword)}
                      helperText={profileFormik.touched.currentPassword && profileFormik.errors.currentPassword}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              edge="end"
                              size="small"
                              aria-label="toggle current password visibility"
                            >
                              {showCurrentPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      id="newPassword"
                      name="newPassword"
                      label="New Password"
                      type={showNewPassword ? "text" : "password"}
                      value={profileFormik.values.newPassword}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}
                      error={profileFormik.touched.newPassword && Boolean(profileFormik.errors.newPassword)}
                      helperText={
                        profileFormik.touched.newPassword &&
                        profileFormik.errors.newPassword &&
                        profileFormik.errors.newPassword !== strongPasswordMessage
                          ? profileFormik.errors.newPassword
                          : undefined
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              edge="end"
                              size="small"
                              aria-label="toggle new password visibility"
                            >
                              {showNewPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      id="confirmPassword"
                      name="confirmPassword"
                      label="Confirm New Password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={profileFormik.values.confirmPassword}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}
                      error={profileFormik.touched.confirmPassword && Boolean(profileFormik.errors.confirmPassword)}
                      helperText={profileFormik.touched.confirmPassword && profileFormik.errors.confirmPassword}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              size="small"
                              aria-label="toggle confirm password visibility"
                            >
                              {showConfirmPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  {profileFormik.touched.newPassword &&
                    profileFormik.errors.newPassword === strongPasswordMessage && (
                    <Grid item xs={12} sx={{ pt: "0 !important" }}>
                      <FormHelperText error sx={{ mx: 1.75, mt: 0.25 }}>
                        {strongPasswordMessage}
                      </FormHelperText>
                    </Grid>
                  )}
              </Grid>
            </LocalizationProvider>
            <FormModalActions
              onCancel={handleCloseEditProfile}
              submitType="submit"
              submitLabel="Save"
              loading={profileFormik.isSubmitting}
            />
        </Box>
      </BasicModal>

      <PatientPrintPreviewDialog
        open={printPreview.open}
        title={printPreview.title}
        documentHtml={printPreview.documentHtml}
        loading={printPreview.loading}
        onClose={closePrintPreview}
        onPrint={() => printPreview.printHandler?.()}
      />

      {/* Homecare Modal */}
      {activeQuickAction?.key === "homecare" && (
        <Dialog
          open={true}
          onClose={handleCloseModal}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Homecare Services</Typography>
              <IconButton onClick={handleCloseModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Alert severity="info">
                Medical care services delivered right to your doorstep. Our homecare services include nursing care, physiotherapy, and medical equipment support.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Please contact our help desk or call the hospital helpline to book homecare services. Our team will assist you in scheduling home visits and arranging necessary medical support.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button 
              onClick={handleCloseModal}
              variant="outlined"
              sx={{ 
                textTransform: "none",
                fontWeight: 600,
                borderColor: theme.palette.grey[400],
                color: theme.palette.text.secondary,
                "&:hover": {
                  borderColor: theme.palette.grey[600],
                  backgroundColor: `${theme.palette.grey[100]}40`,
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
       </Box>
       </Box>
       <PatientPortalFooter
         language={language}
         onFooterLink={(item) => {
           if (item === "Find a Doctor" || item === "Book Appointment") {
             openBookingSearch("book-appointment");
             return true;
           }
           if (item === "OPD" || item === "Appointments") {
             scrollToId("patient-appointments");
             return true;
           }
           if (item === "Pathology" || item === "Laboratory") {
             scrollToId("patient-booked-tests");
             return true;
           }
           if (item === "Pharmacy" || item === "Billing") {
             scrollToId("patient-quick-actions");
             return true;
           }
           return false;
         }}
       />
    </Box>
  );
};

export default PatientDashboard;

